import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';

import {
    Button,
    Card,
    Col,
    Collapse,
    Divider,
    Drawer,
    Empty,
    Form,
    Input,
    InputNumber,
    Modal,
    Row,
    Segmented,
    Select,
    Space,
    Spin,
    Switch,
    Tabs,
    Descriptions,
    Tag,
    Timeline,
    Typography,
    message
} from 'antd';

import {
    AppstoreOutlined,
    CloseOutlined,
    DeleteOutlined,
    EnvironmentOutlined,
    FilterOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    MessageOutlined,
    PlusOutlined,
    ReloadOutlined,
    SaveOutlined,
    UnorderedListOutlined,
    EyeOutlined,
    MailOutlined,
    RollbackOutlined,
    SendOutlined
} from '@ant-design/icons';

import Layout from './Layout';
import TmFieldValueInput from './TmFieldValueInput';
import TaskManagerKanban from './TaskManagerKanban';
import TaskManagerList from './TaskManagerList';
import TaskManagerMap from './TaskManagerMap';
import './TaskManagerFilter.css';
import './TaskManagerDetail.css';
import './TaskManagerPage.css';
import { io } from 'socket.io-client';

import {
    STANDARD_FILTER_FIELDS,
    buildStatusColorMap,
    formatMoscowDateTime,
    normalizeStatusKey,
    resolveAutoExecutorOrgId,
    tmApi
} from '../utils/taskManager';
import {
    getAllowedStatusTargets,
    getBoardStatusNames,
    getFieldAccessLevel,
    resolveStatusOnPersonAssign
} from '../utils/tmWorkflow';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const DEFAULT_PANE_LAYOUT = {
    filterPx: 280,
    detailPx: 420
};

const clampPane = (value, min, max) =>
    Math.max(min, Math.min(max, Number(value) || min));

const StatusTag = ({ status, colorMap }) => {
    const color =
        colorMap[normalizeStatusKey(status)] ||
        '#1677ff';

    return (
        <Tag
            style={{
                margin: 0,
                color: '#fff',
                background: color,
                borderColor: color,
                fontWeight: 600,
                whiteSpace: 'normal'
            }}
        >
            {status || 'Без статуса'}
        </Tag>
    );
};

const cleanFilters = filters => {
    const next = {};

    Object.entries(filters || {}).forEach(
        ([key, value]) => {
            if (
                value === undefined ||
                value === null ||
                value === ''
            ) {
                return;
            }

            if (
                Array.isArray(value) &&
                value.length === 0
            ) {
                return;
            }

            next[key] = Array.isArray(value)
                ? value.join(',')
                : value;
        }
    );

    return next;
};

const READ_ONLY_CF = new Set([
    'Nomer_TT',
    'Nomer_BS',
    'Zakazchik',
    'Adres_BS',
    'Rasstojanie_do_BS'
]);

const TMC_CF = 'Oborudovanija_i_materiale';

const resolveCustomerLabel = (value, customers) => {
    if (!value) {
        return '—';
    }
    const found = (customers || []).find(
        item => item.value === value || item.label === value
    );
    return found?.label || value;
};

const photoPreviewUrl = relativePath => {
    if (!relativePath) {
        return null;
    }
    const token = localStorage.getItem('token') || '';
    return `/api/installer-work/photo?path=${encodeURIComponent(relativePath)}&token=${encodeURIComponent(token)}`;
};

const taskSnapshot = task =>
    JSON.stringify({
        title: task?.title,
        description: task?.description,
        status_name: task?.status_name,
        status_invoices: task?.status_invoices,
        news_type_name: task?.news_type_name,
        region_name: task?.region_name,
        assigned_user_fio: task?.assigned_user_fio,
        assigned_user_id: task?.assigned_user_id,
        fields: (task?.fields || []).map(item => ({
            system_name: item.system_name,
            value: item.value
        })),
        updated_at: task?.updated_at
    });

const TaskManagerPage = () => {
    const [loading, setLoading] = useState(false);
    const [tasks, setTasks] = useState([]);
    const [total, setTotal] = useState(0);
    const [taskAccess, setTaskAccess] = useState(null);
    const [statuses, setStatuses] = useState([]);
    const [customFields, setCustomFields] = useState([]);
    const [viewConfig, setViewConfig] = useState({
        filterFields: [
            'search',
            'status_name',
            'region_name',
            'assigned_user_fio',
            'cf_Nomer_TT',
            'cf_Nomer_BS'
        ],
        filters: {},
        filterTemplates: [],
        boardView: 'kanban',
        paneLayout: { ...DEFAULT_PANE_LAYOUT }
    });
    const [boardView, setBoardView] = useState('kanban');
    const [paneLayout, setPaneLayout] = useState({
        ...DEFAULT_PANE_LAYOUT
    });
    const paneLayoutRef = useRef({ ...DEFAULT_PANE_LAYOUT });
    const [yandexMapsApiKey, setYandexMapsApiKey] =
        useState('');
    const [workflowBundle, setWorkflowBundle] = useState(null);
    const [orgBundle, setOrgBundle] = useState(null);
    const [fieldAccessMap, setFieldAccessMap] = useState({});

    const [filterVisible, setFilterVisible] =
        useState(true);
    const [draftFilters, setDraftFilters] = useState({});
    const [draftFilterFields, setDraftFilterFields] =
        useState([]);
    const [activeFilterTemplateId, setActiveFilterTemplateId] =
        useState(null);
    const [templateModalOpen, setTemplateModalOpen] =
        useState(false);
    const [templateName, setTemplateName] = useState('');
    const [templateSaving, setTemplateSaving] = useState(false);
    const [isMobile, setIsMobile] = useState(() =>
        typeof window !== 'undefined'
            ? window.matchMedia('(max-width: 768px)').matches
            : false
    );
    const [mobileFilterOpen, setMobileFilterOpen] =
        useState(false);

    const [detailOpen, setDetailOpen] = useState(false);
    /*
     * true — детали в выезжающем Drawer (как раньше);
     * false (по умолчанию) — панель справа ~30% экрана
     * (раскладка 15% / 55% / 30%).
     */
    const [detailInDrawer, setDetailInDrawer] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [taskForm] = Form.useForm();
    const [customers, setCustomers] = useState([]);
    const [installers, setInstallers] = useState([]);
    const [timeline, setTimeline] = useState([]);
    const [commentText, setCommentText] = useState('');
    const [bsHistory, setBsHistory] = useState(null);
    const [bsHistorySelectedLog, setBsHistorySelectedLog] =
        useState(null);
    const [bsHistoryPreview, setBsHistoryPreview] = useState(null);
    const [bsHistoryPreviewLoading, setBsHistoryPreviewLoading] =
        useState(false);
    const [commentSending, setCommentSending] = useState(false);
    const [dispatcherChatLoading, setDispatcherChatLoading] =
        useState(false);
    const [statusAnimIds, setStatusAnimIds] = useState(
        () => new Set()
    );
    const [updatedAnimIds, setUpdatedAnimIds] = useState(
        () => new Set()
    );
    const [liveConnected, setLiveConnected] = useState(false);
    const [detailTab, setDetailTab] = useState('main');
    const [reportBundle, setReportBundle] = useState(null);
    const [mailHistory, setMailHistory] = useState(null);
    const [mailHistoryLoading, setMailHistoryLoading] =
        useState(false);
    const [mailComposeOpen, setMailComposeOpen] =
        useState(false);
    const [mailComposeMode, setMailComposeMode] =
        useState('new');
    const [mailComposeReplyId, setMailComposeReplyId] =
        useState(null);
    const [mailSending, setMailSending] = useState(false);
    const [mailComposeForm] = Form.useForm();
    const editingTaskIdRef = useRef(null);
    const detailOpenRef = useRef(false);
    const socketRef = useRef(null);
    const watchedBs = Form.useWatch('cf__Nomer_BS', taskForm);
    const watchedAddress = Form.useWatch('cf__Adres_BS', taskForm);
    const watchedRegion = Form.useWatch('region_name', taskForm);
    const watchedAssignee = Form.useWatch(
        'assigned_user_fio',
        taskForm
    );
    const watchedStatus = Form.useWatch(
        'status_name',
        taskForm
    );
    const watchedTitle = Form.useWatch('title', taskForm);
    const watchedProjectId = Form.useWatch('project_id', taskForm);
    const watchedCreatorOrgId = Form.useWatch(
        'creator_org_id',
        taskForm
    );
    const watchedNewsType = Form.useWatch(
        'news_type_name',
        taskForm
    );

    const colorMap = useMemo(
        () => buildStatusColorMap(statuses),
        [statuses]
    );

    const filterWorkType = useMemo(() => {
        const raw =
            viewConfig?.filters?.news_type_name ||
            draftFilters?.news_type_name ||
            '';
        return String(raw || '').trim() || null;
    }, [viewConfig, draftFilters]);

    const kanbanColumns = useMemo(
        () =>
            getBoardStatusNames({
                tasks,
                statuses,
                workflowBundle,
                filterWorkType
            }),
        [tasks, statuses, workflowBundle, filterWorkType]
    );

    const resolveAllowedTargets = useCallback(
        task =>
            getAllowedStatusTargets(task, workflowBundle),
        [workflowBundle]
    );

    const statusSelectOptions = useMemo(() => {
        const wf = editingTask?.workflow;
        const current =
            watchedStatus || editingTask?.status_name || null;
        if (wf && !wf.unrestricted && Array.isArray(wf.allowed_targets)) {
            const names = new Set(wf.allowed_targets);
            if (current) names.add(current);
            return [...names].map(name => ({
                value: name,
                label: name
            }));
        }
        if (wf?.stages?.length) {
            return wf.stages.map(s => ({
                value: s.status_name,
                label: s.human_name || s.status_name
            }));
        }
        return statuses.map(item => ({
            value: item.name,
            label: item.name
        }));
    }, [editingTask, watchedStatus, statuses]);

    const projectOptions = useMemo(
        () =>
            (orgBundle?.projects || []).map(p => ({
                value: p.id,
                label: p.human_name
            })),
        [orgBundle]
    );

    const creatorOrgOptions = useMemo(() => {
        const orgs = orgBundle?.organizations || [];
        const links = orgBundle?.project_links || [];
        const projectId = Number(watchedProjectId) || 0;
        let list = orgs.filter(o => o.org_type === 'creator');
        if (projectId) {
            const allowed = new Set(
                links
                    .filter(
                        l =>
                            Number(l.project_id) === projectId &&
                            l.binding_role === 'creator'
                    )
                    .map(l => Number(l.organization_id))
            );
            if (allowed.size) {
                list = list.filter(o => allowed.has(Number(o.id)));
            }
        }
        return list.map(o => ({
            value: o.id,
            label: o.human_name
        }));
    }, [orgBundle, watchedProjectId]);

    const executorOrgOptions = useMemo(() => {
        const orgs = orgBundle?.organizations || [];
        const workLinks = orgBundle?.work_links || [];
        const projectLinks = orgBundle?.project_links || [];
        const creatorId = Number(watchedCreatorOrgId) || 0;
        const projectId = Number(watchedProjectId) || 0;
        const workType = String(watchedNewsType || '').trim();

        let list = orgs.filter(o => o.org_type === 'executor');

        if (creatorId && workType) {
            const matched = workLinks.filter(l => {
                if (Number(l.creator_org_id) !== creatorId) {
                    return false;
                }
                if (String(l.work_type) !== workType) {
                    return false;
                }
                const linkProject = Number(l.project_id) || 0;
                if (
                    linkProject &&
                    projectId &&
                    linkProject !== projectId
                ) {
                    return false;
                }
                if (linkProject && !projectId) {
                    return false;
                }
                return true;
            });
            if (matched.length) {
                const ids = new Set(
                    matched.map(l => Number(l.executor_org_id))
                );
                list = list.filter(o => ids.has(Number(o.id)));
            }
        } else if (projectId) {
            const allowed = new Set(
                projectLinks
                    .filter(
                        l =>
                            Number(l.project_id) === projectId &&
                            l.binding_role === 'executor'
                    )
                    .map(l => Number(l.organization_id))
            );
            if (allowed.size) {
                list = list.filter(o => allowed.has(Number(o.id)));
            }
        }

        return list.map(o => ({
            value: o.id,
            label: o.human_name
        }));
    }, [
        orgBundle,
        watchedCreatorOrgId,
        watchedProjectId,
        watchedNewsType
    ]);

    const takeTask = async () => {
        if (!editingTask?.id) return;
        try {
            const { data } = await tmApi.post(
                `/api/task-manager/tasks/${editingTask.id}/take`
            );
            message.success('Заявка взята');
            const task = data.data;
            setEditingTask(prev => ({
                ...prev,
                ...task,
                org_permissions: { can_take: false }
            }));
            applyTaskToForm(task, taskForm);
            loadTasks();
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Не удалось взять заявку'
            );
        }
    };

    const mainFieldVisible = (systemName, mode = 'read') => {
        if (editingTask) {
            const level = getFieldAccessLevel(
                editingTask,
                systemName,
                'edit'
            );
            if (level === 'none') return false;
            if (mode === 'edit') return level === 'edit' || level === 'full';
            return true;
        }
        const level = fieldAccessMap[systemName] || 'edit';
        if (level === 'none') return false;
        if (mode === 'edit') return level === 'edit' || level === 'full';
        return true;
    };

    const mainFieldReadonly = systemName => {
        if (!editingTask) return false;
        const level = getFieldAccessLevel(
            editingTask,
            systemName,
            'edit'
        );
        return level === 'read';
    };

    const loadMeta = useCallback(async () => {
        const [
            metaRes,
            statusRes,
            fieldsRes,
            viewRes,
            workflowRes,
            orgRes
        ] = await Promise.all([
                tmApi.get('/api/task-manager/meta'),
                tmApi.get('/api/task-manager/statuses'),
                tmApi.get('/api/task-manager/fields'),
            tmApi.get('/api/task-manager/my-view'),
            tmApi.get('/api/task-manager/workflow'),
            tmApi
                .get('/api/task-manager/org-bundle')
                .catch(() => ({ data: { data: null } }))
            ]);

        setTaskAccess(metaRes.data?.taskAccess || null);
        setFieldAccessMap(
            metaRes.data?.fieldAccess || {}
        );
        setYandexMapsApiKey(
            metaRes.data?.yandexMapsApiKey || ''
        );
        setStatuses(statusRes.data?.data || []);
        setWorkflowBundle(workflowRes.data?.data || null);
        setOrgBundle(orgRes.data?.data || null);

        const customs = (
            fieldsRes.data?.data || []
        ).filter(item => item.is_active);

        setCustomFields(customs);

        const cfg = viewRes.data?.data?.config || {};
        const defaultFilterFields = [
            'search',
            'status_name',
            'region_name',
            'assigned_user_fio',
            'cf_Nomer_TT',
            'cf_Nomer_BS'
        ];

        const savedView = ['kanban', 'list', 'map'].includes(
            cfg.boardView
        )
            ? cfg.boardView
            : 'kanban';

        const savedPanes = cfg.paneLayout || {};
        const nextPanes = {
            filterPx: clampPane(
                savedPanes.filterPx ?? DEFAULT_PANE_LAYOUT.filterPx,
                200,
                480
            ),
            detailPx: clampPane(
                savedPanes.detailPx ?? DEFAULT_PANE_LAYOUT.detailPx,
                300,
                720
            )
        };

        const next = {
            filterFields:
                cfg.filterFields?.length
                    ? cfg.filterFields
                    : defaultFilterFields,
            filters: cfg.filters || {},
            filterTemplates: Array.isArray(cfg.filterTemplates)
                ? cfg.filterTemplates
                : [],
            boardView: savedView,
            detailInDrawer: Boolean(cfg.detailInDrawer),
            filterVisible:
                cfg.filterVisible === undefined
                    ? true
                    : Boolean(cfg.filterVisible),
            paneLayout: nextPanes
        };

        filtersRef.current = next.filters;
        paneLayoutRef.current = nextPanes;
        setViewConfig(next);
        setBoardView(savedView);
        setDetailInDrawer(Boolean(cfg.detailInDrawer));
        setFilterVisible(next.filterVisible);
        setPaneLayout(nextPanes);
        setDraftFilters(next.filters);
        setDraftFilterFields(next.filterFields);

        if (cfg.filterVisible === false) {
            setFilterVisible(false);
        }

        try {
            const [instRes, dictRes] = await Promise.all([
                tmApi.get('/api/task-manager/lookups/installers'),
                tmApi.get('/api/task-manager/dictionaries')
            ]);
            setInstallers(instRes.data?.data || []);

            const custDict = (dictRes.data?.data || []).find(
                d => d.system_name === 'customers'
            );
            if (custDict?.id) {
                const { data } = await tmApi.get(
                    `/api/task-manager/dictionaries/${custDict.id}/items`
                );
                setCustomers(
                    (data.data || []).map(item => ({
                        value: item.value,
                        label: item.label || item.value
                    }))
                );
            }
        } catch (_) {
            /* lookups optional */
        }
    }, []);

    const mergeTaskUpdates = useCallback(updates => {
        if (!Array.isArray(updates) || !updates.length) {
            return [];
        }

        const statusChanged = [];
        const fieldChanged = [];
        const changedIds = [];
        const createdIds = [];

        setTasks(prev => {
            const map = new Map(prev.map(item => [item.id, item]));

            updates.forEach(incoming => {
                if (!incoming?.id) {
                    return;
                }

                const old = map.get(incoming.id);
                const task = old
                    ? {
                          ...old,
                          ...incoming,
                          fields:
                              incoming.fields?.length
                                  ? incoming.fields
                                  : old.fields
                      }
                    : incoming;

                if (!old) {
                    createdIds.push(task.id);
                    fieldChanged.push(task.id);
                    changedIds.push(task.id);
                } else if (old.status_name !== task.status_name) {
                    statusChanged.push(task.id);
                    changedIds.push(task.id);
                } else if (
                    taskSnapshot(old) !== taskSnapshot(task)
                ) {
                    fieldChanged.push(task.id);
                    changedIds.push(task.id);
                }
                map.set(task.id, task);
            });

            return Array.from(map.values()).sort(
                (a, b) => b.id - a.id
            );
        });

        if (createdIds.length) {
            setTotal(prev => prev + createdIds.length);
        }

        if (statusChanged.length) {
            setStatusAnimIds(prev => {
                const next = new Set(prev);
                statusChanged.forEach(id => next.add(id));
                return next;
            });
            window.setTimeout(() => {
                setStatusAnimIds(prev => {
                    const next = new Set(prev);
                    statusChanged.forEach(id => next.delete(id));
                    return next;
                });
            }, 700);
        }

        if (fieldChanged.length) {
            setUpdatedAnimIds(prev => {
                const next = new Set(prev);
                fieldChanged.forEach(id => next.add(id));
                return next;
            });
            window.setTimeout(() => {
                setUpdatedAnimIds(prev => {
                    const next = new Set(prev);
                    fieldChanged.forEach(id => next.delete(id));
                    return next;
                });
            }, 600);
        }

        return changedIds;
    }, []);

    const removeTaskFromBoard = useCallback(taskId => {
        const id = Number(taskId);
        if (!id) {
            return;
        }

        setTasks(prev => prev.filter(item => item.id !== id));
        setTotal(prev => Math.max(0, prev - 1));

        if (
            detailOpenRef.current &&
            Number(editingTaskIdRef.current) === id
        ) {
            setDetailOpen(false);
            setEditingTask(null);
        }
    }, []);

    const cfValue = (task, name) => {
        const field = (task?.fields || []).find(
            item => item.system_name === name
        );
        return field?.value;
    };

    const formatDistanceKm = value => {
        if (value == null || value === '') {
            return '—';
        }
        const n = Number(value);
        if (Number.isFinite(n)) {
            return `${n} км`;
        }
        return String(value);
    };

    const mapUrlForTask = task => {
        const coords = task?.meta?.coordinates;
        const address = cfValue(task, 'Adres_BS') || '';

        if (coords?.lat && coords?.lon) {
            return `https://yandex.ru/maps/?pt=${coords.lon},${coords.lat}&z=16&l=map`;
        }

        if (address) {
            return `https://yandex.ru/maps/?text=${encodeURIComponent(address)}`;
        }

        return null;
    };

    const previewCustomerLabel = task =>
        resolveCustomerLabel(
            task?.region_name || cfValue(task, 'Zakazchik'),
            customers
        );

    const BS_PREVIEW_META_CF = new Set([
        'Nomer_BS',
        'Nomer_TT',
        'Zakazchik',
        'Adres_BS',
        'Rasstojanie_do_BS'
    ]);

    const renderBsHistoryMetaBlock = ({
        bsNumber,
        newsDate,
        customer,
        address,
        distanceKm,
        mapHref
    }) => (
        <div className="tm-detail-meta tm-bs-history-preview-meta">
            <div className="tm-detail-meta-item">
                <span className="tm-detail-meta-label">Номер БС</span>
                <span className="tm-detail-meta-value">
                    {bsNumber || '—'}
                </span>
            </div>
            <div className="tm-detail-meta-item">
                <span className="tm-detail-meta-label">
                    Дата заявки
                </span>
                <span className="tm-detail-meta-value">
                    {newsDate
                        ? formatMoscowDateTime(newsDate)
                        : '—'}
                </span>
            </div>
            <div className="tm-detail-meta-item tm-detail-meta-item--wide">
                <span className="tm-detail-meta-label">Заказчик</span>
                <span className="tm-detail-meta-value">
                    {customer || '—'}
                </span>
            </div>
            <div className="tm-detail-meta-item tm-detail-meta-item--wide">
                <span className="tm-detail-meta-label">Адрес БС</span>
                <span className="tm-detail-meta-value">
                    {address || '—'}
                    {mapHref ? (
                        <>
                            {' '}
                            <a
                                href={mapHref}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <EnvironmentOutlined /> Карта
                            </a>
                        </>
                    ) : null}
                </span>
            </div>
            <div className="tm-detail-meta-item">
                <span className="tm-detail-meta-label">
                    Расстояние до БС
                </span>
                <span className="tm-detail-meta-value">
                    {formatDistanceKm(distanceKm)}
                </span>
            </div>
        </div>
    );

    const formatTmFieldDisplayValue = field => {
        if (
            field == null ||
            field.value === '' ||
            field.value == null
        ) {
            return '—';
        }
        if (field.value_type === 'boolean') {
            return field.value ? 'Да' : 'Нет';
        }
        if (
            field.value_type === 'date' ||
            field.value_type === 'datetime'
        ) {
            return formatMoscowDateTime(field.value, {
                dateOnly: field.value_type === 'date',
                withSeconds: false
            });
        }
        if (Array.isArray(field.value)) {
            return field.value.length
                ? field.value.join(', ')
                : '—';
        }
        if (typeof field.value === 'object') {
            return JSON.stringify(field.value);
        }
        return String(field.value);
    };

    const renderBsHistoryPreview = () => {
        if (!bsHistorySelectedLog) {
            return null;
        }
        if (bsHistoryPreviewLoading) {
            return (
                <div className="tm-bs-history-preview-body">
                    <Spin size="small" />
                </div>
            );
        }
        if (bsHistoryPreview?.kind === 'current') {
            const mapHref = mapUrl();
            return (
                <div className="tm-bs-history-preview-body">
                    {renderBsHistoryMetaBlock({
                        bsNumber: cfValue(editingTask, 'Nomer_BS'),
                        newsDate: editingTask?.news_date,
                        customer: customerLabel,
                        address: cfValue(editingTask, 'Adres_BS'),
                        distanceKm: cfValue(
                            editingTask,
                            'Rasstojanie_do_BS'
                        ),
                        mapHref
                    })}
                    <Text type="secondary">
                        Это текущая открытая заявка — остальные
                        поля редактируются на вкладке «Основное».
                    </Text>
                </div>
            );
        }
        if (bsHistoryPreview?.kind === 'tm_task') {
            const task = bsHistoryPreview.task;
            const customFields = (task?.fields || []).filter(
                field =>
                    field.field_kind === 'custom' &&
                    !BS_PREVIEW_META_CF.has(field.system_name) &&
                    field.value != null &&
                    field.value !== ''
            );
            const mapHref = mapUrlForTask(task);
            return (
                <div className="tm-bs-history-preview-body">
                    {renderBsHistoryMetaBlock({
                        bsNumber: cfValue(task, 'Nomer_BS'),
                        newsDate: task?.news_date,
                        customer: previewCustomerLabel(task),
                        address: cfValue(task, 'Adres_BS'),
                        distanceKm: cfValue(task, 'Rasstojanie_do_BS'),
                        mapHref
                    })}
                    <div className="tm-detail-preview-section">
                        <div className="tm-detail-group-title">
                            Заявка
                        </div>
                        {renderReadonlyFieldGrid([
                            {
                                key: 'tt',
                                label: 'Номер ТТ',
                                value: task?.task_number
                            },
                            {
                                key: 'status',
                                label: 'Статус',
                                value: task?.status_name
                            },
                            {
                                key: 'type',
                                label: 'Тип',
                                value: task?.news_type_name
                            },
                            {
                                key: 'assignee',
                                label: 'Исполнитель',
                                value: task?.assigned_user_fio
                            },
                            {
                                key: 'created',
                                label: 'Создана',
                                value: formatMoscowDateTime(
                                    task?.created_at
                                )
                            },
                            {
                                key: 'title',
                                label: 'Название',
                                value: task?.title,
                                wide: true
                            },
                            ...customFields.map(field => ({
                                key: field.system_name,
                                label:
                                    field.human_name ||
                                    field.system_name,
                                value: formatTmFieldDisplayValue(
                                    field
                                ),
                                wide:
                                    field.value_type === 'text' ||
                                    String(
                                        formatTmFieldDisplayValue(
                                            field
                                        )
                                    ).length > 48
                            }))
                        ])}
                    </div>
                </div>
            );
        }
        const log =
            bsHistoryPreview?.log || bsHistorySelectedLog;
        return (
            <div className="tm-bs-history-preview-body">
                {renderBsHistoryMetaBlock({
                    bsNumber: bsHistory?.bs_number,
                    newsDate: log?.news_date,
                    customer: log?.zakazchik,
                    address: null,
                    distanceKm: null,
                    mapHref: null
                })}
                <div className="tm-detail-preview-section">
                    <div className="tm-detail-group-title">
                        Заявка
                    </div>
                    {renderReadonlyFieldGrid([
                        {
                            key: 'tt',
                            label: 'Номер ТТ',
                            value: log?.task_number
                        },
                        {
                            key: 'title',
                            label: 'Название',
                            value: log?.title,
                            wide: true
                        },
                        {
                            key: 'work',
                            label: 'Выполненные работы',
                            value: log?.work_summary,
                            wide: true
                        },
                        {
                            key: 'source',
                            label: 'Источник',
                            value:
                                log?.source === 'legacy_task'
                                    ? 'Архивная заявка'
                                    : 'Task Manager'
                        }
                    ])}
                </div>
            </div>
        );
    };

    const openBsHistoryPreview = useCallback(
        async log => {
            if (!log) return;
            if (bsHistorySelectedLog?.id === log.id) {
                setBsHistorySelectedLog(null);
                setBsHistoryPreview(null);
                return;
            }
            setBsHistorySelectedLog(log);
            setBsHistoryPreview(null);
            if (log.source === 'tm_task' && log.source_id) {
                if (log.source_id === editingTask?.id) {
                    setBsHistoryPreview({ kind: 'current' });
                    return;
                }
                setBsHistoryPreviewLoading(true);
                try {
                    const { data } = await tmApi.get(
                        `/api/task-manager/tasks/${log.source_id}`
                    );
                    setBsHistoryPreview({
                        kind: 'tm_task',
                        task: data.data,
                    });
                } catch (_) {
                    setBsHistoryPreview({
                        kind: 'legacy',
                        log,
                    });
                } finally {
                    setBsHistoryPreviewLoading(false);
                }
            } else {
                setBsHistoryPreview({ kind: 'legacy', log });
            }
        },
        [bsHistorySelectedLog?.id, editingTask?.id]
    );

    const loadReportBundle = useCallback(async taskId => {
        if (!taskId) {
            setReportBundle(null);
            return;
        }

        try {
            const { data } = await tmApi.get(
                `/api/task-manager/tasks/${taskId}/report`
            );
            setReportBundle(data.data || null);
        } catch (_) {
            setReportBundle(null);
        }
    }, []);

    const loadMailHistory = useCallback(
        async (taskId, { refresh = true } = {}) => {
            if (!taskId) {
                setMailHistory(null);
                return;
            }

            setMailHistoryLoading(true);

            try {
                const { data } = await tmApi.get(
                    `/api/task-manager/tasks/${taskId}/mails`,
                    {
                        params: {
                            refresh: refresh ? 1 : 0
                        }
                    }
                );
                setMailHistory(data.data || null);
            } catch (error) {
                setMailHistory(null);
                message.error(
                    error?.response?.data?.message ||
                        'Не удалось загрузить историю почты'
                );
            } finally {
                setMailHistoryLoading(false);
            }
        },
        []
    );

    const openMailCompose = useCallback(
        (mode = 'new', mail = null) => {
            const wo =
                mailHistory?.wo ||
                editingTask?.task_number ||
                cfValue(editingTask, 'Nomer_TT') ||
                '';

            if (mode === 'reply' && mail) {
                const subject = /^re:/i.test(
                    String(mail.subject || '')
                )
                    ? mail.subject
                    : `Re: ${mail.subject || wo || ''}`.trim();

                const quoteHeader = [
                    '',
                    '',
                    `--- Исходное письмо${
                        mail.received_at
                            ? ` ${formatMoscowDateTime(mail.received_at)}`
                            : ''
                    }${
                        mail.from_email
                            ? ` от ${mail.from_email}`
                            : ''
                    } ---`,
                    ''
                ].join('\n');

                const quoted = String(
                    mail.text_body || ''
                )
                    .split('\n')
                    .map(line => `> ${line}`)
                    .join('\n');

                setMailComposeMode('reply');
                setMailComposeReplyId(mail.id);
                mailComposeForm.setFieldsValue({
                    to: mail.from_email || '',
                    cc: '',
                    subject,
                    text: quoteHeader + quoted
                });
            } else {
                const lastInbound = (
                    mailHistory?.items || []
                )
                    .slice()
                    .reverse()
                    .find(
                        item =>
                            item.from_email &&
                            item.parsed?.direction !==
                                'outbound' &&
                            !String(
                                item.status_label || ''
                            ).includes('Исходящ')
                    );

                setMailComposeMode('new');
                setMailComposeReplyId(null);
                mailComposeForm.setFieldsValue({
                    to:
                        lastInbound?.from_email ||
                        'noc.rostov@info.t2.ru',
                    cc: '',
                    subject: wo
                        ? `${wo}${
                              cfValue(
                                  editingTask,
                                  'Nomer_BS'
                              )
                                  ? `/${cfValue(editingTask, 'Nomer_BS')}`
                                  : ''
                          }`
                        : '',
                    text: ''
                });
            }

            setMailComposeOpen(true);
        },
        [
            mailHistory,
            editingTask,
            mailComposeForm
        ]
    );

    const submitMailCompose = useCallback(async () => {
        if (!editingTask?.id) {
            return;
        }

        try {
            const values =
                await mailComposeForm.validateFields();

            setMailSending(true);

            await tmApi.post(
                `/api/task-manager/tasks/${editingTask.id}/mails/send`,
                {
                    to: values.to,
                    cc: values.cc || '',
                    subject: values.subject,
                    text: values.text,
                    reply_to_mail_id:
                        mailComposeMode === 'reply'
                            ? mailComposeReplyId
                            : null
                }
            );

            message.success(
                mailComposeMode === 'reply'
                    ? 'Ответ отправлен'
                    : 'Письмо отправлено'
            );
            setMailComposeOpen(false);
            await loadMailHistory(editingTask.id, {
                refresh: false
            });
        } catch (error) {
            if (error?.errorFields) {
                return;
            }

            message.error(
                error?.response?.data?.message ||
                    'Не удалось отправить письмо'
            );
        } finally {
            setMailSending(false);
        }
    }, [
        editingTask?.id,
        mailComposeForm,
        mailComposeMode,
        mailComposeReplyId,
        loadMailHistory
    ]);

    const loadTaskExtras = useCallback(async task => {
        if (!task?.id) {
            setTimeline([]);
            setBsHistory(null);
            setBsHistorySelectedLog(null);
            setBsHistoryPreview(null);
            return;
        }

        try {
            const { data } = await tmApi.get(
                `/api/task-manager/tasks/${task.id}/timeline`
            );
            setTimeline(data.data || []);
        } catch (_) {
            setTimeline([]);
        }

        const bs = cfValue(task, 'Nomer_BS');
        if (bs) {
            try {
                const { data } = await tmApi.get(
                    `/api/task-manager/bs-history/${encodeURIComponent(bs)}`
                );
                setBsHistory(data.data || null);
                setBsHistorySelectedLog(null);
                setBsHistoryPreview(null);
            } catch (_) {
                setBsHistory(null);
            }
        } else {
            setBsHistory(null);
        }
    }, []);

    const applyTaskToForm = useCallback((task, form) => {
        const values = {
            title: task.title,
            description: task.description,
            status: task.status,
            status_name: task.status_name,
            status_invoices: task.status_invoices,
            type: task.type,
            news_type_name: task.news_type_name,
            project_id: task.project_id || undefined,
            creator_org_id: task.creator_org_id || undefined,
            executor_org_id: task.executor_org_id || undefined,
            region_name: task.region_name,
            assigned_user_id: task.assigned_user_id,
            assigned_user_fio: task.assigned_user_fio
        };

        (task.fields || []).forEach(field => {
            if (field.field_kind === 'custom') {
                values[`cf__${field.system_name}`] =
                    field.value;
            }
        });

        form.setFieldsValue(values);
    }, []);

    const refreshOpenTask = useCallback(
        async (taskId, { force = false } = {}) => {
            if (!detailOpenRef.current) {
                return;
            }
            if (
                editingTaskIdRef.current &&
                Number(editingTaskIdRef.current) !==
                    Number(taskId)
            ) {
                return;
            }
            if (!force && taskForm.isFieldsTouched()) {
                return;
            }

            try {
                const { data } = await tmApi.get(
                    `/api/task-manager/tasks/${taskId}`
                );
                const task = data.data;
                setEditingTask(task);
                applyTaskToForm(task, taskForm);
                loadTaskExtras(task);
            } catch (_) {
                /* ignore live refresh errors */
            }
        },
        [applyTaskToForm, loadTaskExtras, taskForm]
    );

    const filtersRef = useRef(viewConfig.filters);
    const skipAutoLoadRef = useRef(false);

    useEffect(() => {
        const mq = window.matchMedia('(max-width: 768px)');
        const onChange = event => setIsMobile(event.matches);
        setIsMobile(mq.matches);
        if (mq.addEventListener) {
            mq.addEventListener('change', onChange);
            return () => mq.removeEventListener('change', onChange);
        }
        mq.addListener(onChange);
        return () => mq.removeListener(onChange);
    }, []);

    useEffect(() => {
        filtersRef.current = viewConfig.filters;
    }, [viewConfig.filters]);

    const loadTasks = useCallback(async (filtersOverride) => {
        setLoading(true);

        try {
            const sourceFilters =
                filtersOverride !== undefined
                    ? filtersOverride
                    : filtersRef.current;
            const params = {
                page: 1,
                limit: 500,
                ...cleanFilters(sourceFilters)
            };

            const { data } = await tmApi.get(
                '/api/task-manager/tasks',
                { params }
            );

            setTasks(data.data || []);
            setTotal(data.total || 0);
            if (data.taskAccess) {
                setTaskAccess(data.taskAccess);
            }
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка загрузки задач'
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadMeta().catch(() => undefined);
    }, [loadMeta]);

    useEffect(() => {
        if (skipAutoLoadRef.current) {
            skipAutoLoadRef.current = false;
            return;
        }
        loadTasks();
    }, [loadTasks, viewConfig.filters]);

    useEffect(() => {
        detailOpenRef.current = detailOpen;
    }, [detailOpen]);

    useEffect(() => {
        editingTaskIdRef.current = editingTask?.id || null;
    }, [editingTask?.id]);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            return undefined;
        }

        const socket = io({
            path: '/api/socket.io',
            auth: { token },
            transports: ['websocket', 'polling']
        });
        socketRef.current = socket;

        const onConnect = () => {
            if (!navigator.onLine) {
                setLiveConnected(false);
                return;
            }
            setLiveConnected(true);
            socket.emit('tm:subscribe');
        };

        const onDisconnect = () => {
            setLiveConnected(false);
        };

        const onConnectError = () => {
            setLiveConnected(false);
        };

        const onBrowserOffline = () => {
            setLiveConnected(false);
        };

        const onBrowserOnline = () => {
            if (!socket.connected) {
                setLiveConnected(false);
            }
        };

        const onTaskEvent = async payload => {
            const action = payload?.action;
            const taskId = Number(payload?.taskId);

            if (action === 'deleted') {
                removeTaskFromBoard(taskId);
                return;
            }

            if (payload?.task?.id) {
                const changedIds = mergeTaskUpdates([
                    payload.task
                ]);

                if (
                    detailOpenRef.current &&
                    editingTaskIdRef.current &&
                    changedIds.includes(
                        editingTaskIdRef.current
                    )
                ) {
                    await refreshOpenTask(
                        editingTaskIdRef.current
                    );
                }
                return;
            }

            if (taskId) {
                try {
                    const { data } = await tmApi.get(
                        `/api/task-manager/tasks/${taskId}`
                    );
                    if (data?.data) {
                        mergeTaskUpdates([data.data]);
                        if (
                            detailOpenRef.current &&
                            Number(
                                editingTaskIdRef.current
                            ) === taskId
                        ) {
                            await refreshOpenTask(taskId);
                        }
                    }
                } catch (_) {
                    /* ignore */
                }
            }
        };

        const onTimelineEvent = async payload => {
            const taskId = Number(payload?.taskId);
            if (
                !detailOpenRef.current ||
                Number(editingTaskIdRef.current) !== taskId
            ) {
                return;
            }

            try {
                const { data } = await tmApi.get(
                    `/api/task-manager/tasks/${taskId}/timeline`
                );
                setTimeline(data.data || []);
            } catch (_) {
                /* ignore */
            }
        };

        socket.on('connect', onConnect);
        socket.on('disconnect', onDisconnect);
        socket.on('connect_error', onConnectError);
        socket.on('tm:task', onTaskEvent);
        socket.on('tm:timeline', onTimelineEvent);
        window.addEventListener('offline', onBrowserOffline);
        window.addEventListener('online', onBrowserOnline);

        if (!navigator.onLine) {
            setLiveConnected(false);
        } else if (socket.connected) {
            onConnect();
        }

        return () => {
            socket.emit('tm:unsubscribe');
            socket.off('connect', onConnect);
            socket.off('disconnect', onDisconnect);
            socket.off('connect_error', onConnectError);
            socket.off('tm:task', onTaskEvent);
            socket.off('tm:timeline', onTimelineEvent);
            window.removeEventListener('offline', onBrowserOffline);
            window.removeEventListener('online', onBrowserOnline);
            socket.disconnect();
            socketRef.current = null;
            setLiveConnected(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mergeTaskUpdates, refreshOpenTask, removeTaskFromBoard]);

    useEffect(() => {
        if (!detailOpen || !orgBundle) {
            return;
        }
        const creatorId = Number(watchedCreatorOrgId) || 0;
        const workType = String(watchedNewsType || '').trim();
        const projectId = Number(watchedProjectId) || 0;
        if (!creatorId || !workType) {
            return;
        }

        const current = taskForm.getFieldValue('executor_org_id');
        const allowedIds = new Set(
            executorOrgOptions.map(item => Number(item.value))
        );

        if (current && allowedIds.has(Number(current))) {
            return;
        }

        const autoId = resolveAutoExecutorOrgId({
            creatorOrgId: creatorId,
            workType,
            projectId,
            workLinks: orgBundle.work_links || []
        });

        if (autoId) {
            taskForm.setFieldsValue({
                executor_org_id: autoId
            });
        } else if (current && !allowedIds.has(Number(current))) {
            taskForm.setFieldsValue({
                executor_org_id: undefined
            });
        }
    }, [
        watchedCreatorOrgId,
        watchedNewsType,
        watchedProjectId,
        detailOpen,
        orgBundle,
        executorOrgOptions,
        taskForm
    ]);

    useEffect(() => {
        if (!detailOpen) return;
        if (watchedRegion) {
            taskForm.setFieldsValue({
                cf__Zakazchik: watchedRegion
            });
        }
    }, [watchedRegion, detailOpen, taskForm]);

    useEffect(() => {
        if (!detailOpen) return;
        const found = installers.find(
            item =>
                item.value === watchedAssignee ||
                item.label === watchedAssignee
        );
        if (found?.id) {
            taskForm.setFieldsValue({
                assigned_user_id: found.id
            });
        }
    }, [watchedAssignee, detailOpen, installers, taskForm]);

    useEffect(() => {
        if (!detailOpen || !watchedBs) {
            return;
        }
        onBsChange(watchedBs);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchedBs, detailOpen]);

    const availableFilterOptions = useMemo(() => {
        const base = STANDARD_FILTER_FIELDS.map(
            item => ({
                value: item.key,
                label: item.label
            })
        );

        const customs = customFields
            .filter(item => item.field_kind === 'custom')
            .map(item => ({
            value: `cf_${item.system_name}`,
            label: item.human_name || item.system_name
        }));

        return [...base, ...customs];
    }, [customFields]);

    const applyFilters = async (
        {
            closePanel = false,
            fieldsOverride,
            filtersOverride,
            silent = false
        } = {}
    ) => {
        const nextFilters =
            filtersOverride !== undefined
                ? { ...filtersOverride }
                : { ...draftFilters };
        const nextFields =
            fieldsOverride !== undefined
                ? [...fieldsOverride]
                : [...draftFilterFields];
        const next = {
            ...viewConfig,
            filters: nextFilters,
            filterFields: nextFields,
            filterTemplates: viewConfig.filterTemplates || [],
            filterVisible: closePanel ? false : filterVisible,
            boardView,
            detailInDrawer,
            paneLayout: paneLayoutRef.current
        };

        skipAutoLoadRef.current = true;
        filtersRef.current = nextFilters;
        setDraftFilters(nextFilters);
        setDraftFilterFields(nextFields);
        setViewConfig(next);

        if (closePanel) {
            setFilterVisible(false);
        }

        await loadTasks(nextFilters);

        if (isMobile) {
            setMobileFilterOpen(false);
        }

        try {
            await tmApi.put('/api/task-manager/my-view', {
                config: next
            });
            if (!silent) {
                message.success('Фильтр применён');
            }
        } catch (error) {
            if (!silent) {
            message.warning(
                'Фильтр применён, сохранить вид не удалось'
            );
            }
        }
    };

    const filterTemplates = viewConfig.filterTemplates || [];

    const persistFilterTemplates = async templates => {
        const next = {
            ...viewConfig,
            filterTemplates: templates,
            filterFields: draftFilterFields,
            filters: draftFilters,
            boardView,
            detailInDrawer,
            filterVisible,
            paneLayout: paneLayoutRef.current
        };
        setViewConfig(next);
        await tmApi.put('/api/task-manager/my-view', {
            config: next
        });
    };

    const toggleFilterVisible = nextVisible => {
        setFilterVisible(nextVisible);
        persistViewConfig({
            filterVisible: nextVisible
        });
    };

    const saveFilterTemplate = async () => {
        const name = String(templateName || '').trim();
        if (!name) {
            message.warning('Введите название шаблона');
            return;
        }
        setTemplateSaving(true);
        try {
            const id = `ft_${Date.now()}`;
            const item = {
                id,
                name,
                filterFields: [...draftFilterFields],
                filters: { ...draftFilters },
                createdAt: new Date().toISOString()
            };
            const nextTemplates = [...filterTemplates, item];
            await persistFilterTemplates(nextTemplates);
            setActiveFilterTemplateId(id);
            setTemplateModalOpen(false);
            setTemplateName('');
            message.success('Шаблон фильтра сохранён');
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Не удалось сохранить шаблон'
            );
        } finally {
            setTemplateSaving(false);
        }
    };

    const applyFilterTemplate = async templateId => {
        const item = filterTemplates.find(
            t => t.id === templateId
        );
        if (!item) {
            setActiveFilterTemplateId(null);
            return;
        }
        setActiveFilterTemplateId(item.id);
        await applyFilters({
            fieldsOverride: item.filterFields || [],
            filtersOverride: item.filters || {},
            silent: true
        });
        message.success(`Шаблон «${item.name}» применён`);
    };

    const deleteFilterTemplate = async templateId => {
        const id = templateId || activeFilterTemplateId;
        if (!id) {
            message.warning('Выберите шаблон');
            return;
        }
        const item = filterTemplates.find(t => t.id === id);
        Modal.confirm({
            title: 'Удалить шаблон?',
            content: item?.name
                ? `«${item.name}» будет удалён`
                : 'Шаблон будет удалён',
            okText: 'Удалить',
            okType: 'danger',
            cancelText: 'Отмена',
            onOk: async () => {
                const nextTemplates = filterTemplates.filter(
                    t => t.id !== id
                );
                await persistFilterTemplates(nextTemplates);
                if (activeFilterTemplateId === id) {
                    setActiveFilterTemplateId(null);
                }
                message.success('Шаблон удалён');
            }
        });
    };

    const persistViewConfig = useCallback(
        async patch => {
            const next = {
                ...viewConfig,
                boardView,
                detailInDrawer,
                filterVisible,
                paneLayout: paneLayoutRef.current,
                ...patch
            };
            setViewConfig(next);
            try {
                await tmApi.put('/api/task-manager/my-view', {
                    config: next
                });
            } catch (_) {
                /* ignore */
            }
            return next;
        },
        [viewConfig, boardView, detailInDrawer, filterVisible]
    );

    const startPaneResize = useCallback(
        (which, event) => {
            event.preventDefault();
            event.stopPropagation();
            const startX = event.clientX;
            const start =
                which === 'filter'
                    ? paneLayoutRef.current.filterPx
                    : paneLayoutRef.current.detailPx;
            const totalWidth =
                event.currentTarget.parentElement
                    ?.clientWidth || window.innerWidth;

            document.body.classList.add('tm-pane-resizing');

            const onMove = moveEvent => {
                const delta = moveEvent.clientX - startX;
                if (which === 'filter') {
                    const nextFilter = clampPane(
                        start + delta,
                        200,
                        Math.min(480, totalWidth * 0.45)
                    );
                    const next = {
                        ...paneLayoutRef.current,
                        filterPx: nextFilter
                    };
                    paneLayoutRef.current = next;
                    setPaneLayout(next);
                    return;
                }

                const nextDetail = clampPane(
                    start - delta,
                    300,
                    Math.min(720, totalWidth * 0.55)
                );
                const next = {
                    ...paneLayoutRef.current,
                    detailPx: nextDetail
                };
                paneLayoutRef.current = next;
                setPaneLayout(next);
            };

            const onUp = () => {
                document.body.classList.remove(
                    'tm-pane-resizing'
                );
                window.removeEventListener(
                    'mousemove',
                    onMove
                );
                window.removeEventListener('mouseup', onUp);
                persistViewConfig({
                    paneLayout: { ...paneLayoutRef.current }
                });
            };

            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', onUp);
        },
        [persistViewConfig]
    );

    const changeBoardView = nextView => {
        if (!['kanban', 'list', 'map'].includes(nextView)) {
            return;
        }

        React.startTransition(() => {
            setBoardView(nextView);
            setViewConfig(prev => ({
                ...prev,
                boardView: nextView,
                detailInDrawer,
                paneLayout: paneLayoutRef.current
            }));
        });

        tmApi
            .put('/api/task-manager/my-view', {
                config: {
                    ...viewConfig,
                    boardView: nextView,
                    detailInDrawer,
                    filterVisible,
                    paneLayout: paneLayoutRef.current
                }
            })
            .catch(() => undefined);
    };

    const changeDetailMode = async checked => {
        const nextMode = Boolean(checked);
        setDetailInDrawer(nextMode);
        await persistViewConfig({
            detailInDrawer: nextMode
        });
    };

    const resetFilters = async () => {
        skipAutoLoadRef.current = true;
        filtersRef.current = {};
        setDraftFilters({});
        setViewConfig(prev => ({
            ...prev,
            filters: {}
        }));
        await loadTasks({});
        if (isMobile) {
            setMobileFilterOpen(false);
        }
        try {
            await tmApi.put('/api/task-manager/my-view', {
                config: {
                    ...viewConfig,
                    filters: {},
                    filterFields: draftFilterFields,
                    filterVisible,
                    boardView,
                    detailInDrawer
                }
            });
        } catch (_) {
            /* ignore */
        }
    };

    const sendComment = async () => {
        if (!editingTask?.id || !commentText.trim()) {
            return;
        }

        setCommentSending(true);
        try {
            await tmApi.post(
                `/api/task-manager/tasks/${editingTask.id}/comments`,
                { text: commentText }
            );
            setCommentText('');
            const { data } = await tmApi.get(
                `/api/task-manager/tasks/${editingTask.id}/timeline`
            );
            setTimeline(data.data || []);
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Не удалось отправить комментарий'
            );
        } finally {
            setCommentSending(false);
        }
    };

    const openDispatcherChat = async () => {
        if (!editingTask?.id) {
            message.warning('Сначала сохраните задачу');
            return;
        }
        setDispatcherChatLoading(true);
        try {
            const { data } = await tmApi.post(
                '/api/chat/from-task',
                { task_id: editingTask.id }
            );
            const roomId = data?.room?.id;
            if (!roomId) {
                throw new Error('Чат не создан');
            }
            window.dispatchEvent(
                new CustomEvent('crm-chat-open', {
                    detail: { roomId }
                })
            );
            message.success('Открыт чат по задаче');
        } catch (error) {
            message.error(
                error?.response?.data?.error ||
                    error?.message ||
                    'Не удалось открыть чат'
            );
        } finally {
            setDispatcherChatLoading(false);
        }
    };

    const onInstallerChange = value => {
        const found = installers.find(
            item => item.value === value || item.label === value
        );
        const patch = {
            assigned_user_fio: value,
            assigned_user_id: found?.id || null
        };
        if (found?.id) {
            const autoStatus = resolveStatusOnPersonAssign(
                {
                    ...editingTask,
                    status_name:
                        taskForm.getFieldValue('status_name') ||
                        editingTask?.status_name
                },
                workflowBundle
            );
            if (autoStatus) {
                patch.status_name = autoStatus;
                patch.status = autoStatus;
            }
        }
        taskForm.setFieldsValue(patch);
    };

    const onBsChange = async value => {
        if (!value) return;

        try {
            const { data } = await tmApi.get(
                `/api/task-manager/lookups/bs/${encodeURIComponent(value)}`
            );
            const item = data.data;
            if (!item) return;

            const patch = {};
            if (item.distance_km != null && item.distance_km !== '') {
                patch.cf__Rasstojanie_do_BS = item.distance_km;
            }
            const currentAddress =
                taskForm.getFieldValue('cf__Adres_BS');
            if (item.address && !currentAddress) {
                patch.cf__Adres_BS = item.address;
            }
            if (Object.keys(patch).length) {
                taskForm.setFieldsValue(patch);
            }

            try {
                const hist = await tmApi.get(
                    `/api/task-manager/bs-history/${encodeURIComponent(value)}`
                );
                setBsHistory(hist.data?.data || null);
                setBsHistorySelectedLog(null);
                setBsHistoryPreview(null);
            } catch (_) {
                /* ignore */
            }
        } catch (_) {
            /* ignore */
        }
    };

    const mapUrl = () => {
        const coords = editingTask?.meta?.coordinates;
        const address =
            watchedAddress ||
            cfValue(editingTask, 'Adres_BS') ||
            '';

        if (coords?.lat && coords?.lon) {
            return `https://yandex.ru/maps/?pt=${coords.lon},${coords.lat}&z=16&l=map`;
        }

        if (address) {
            return `https://yandex.ru/maps/?text=${encodeURIComponent(address)}`;
        }

        return null;
    };

    const openCreate = () => {
        setEditingTask(null);
        setDetailTab('main');
        setReportBundle(null);
        setMailHistory(null);
        taskForm.resetFields();
        taskForm.setFieldsValue({
            status: 'new',
            status_invoices: 'new',
            type: 'ordinary'
        });
        setDetailOpen(true);
    };

    const openEdit = async record => {
        try {
            const { data } = await tmApi.get(
                `/api/task-manager/tasks/${record.id}`
            );
            const task = data.data;
            setEditingTask(task);
            applyTaskToForm(task, taskForm);
            setDetailTab('main');
            setMailHistory(null);
            setDetailOpen(true);
            loadTaskExtras(task);
            loadReportBundle(task.id);
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Не удалось открыть задачу'
            );
        }
    };

    const onKanbanStatusChange = async (taskId, statusName) => {
        const task = tasks.find(
            item => String(item.id) === String(taskId)
        );
        if (!task) {
            return;
        }

        const allowed = getAllowedStatusTargets(
            task,
            workflowBundle
        );
        if (
            Array.isArray(allowed) &&
            !allowed.length &&
            !task.assigned_user_id &&
            !String(task.assigned_user_fio || '').trim()
        ) {
            message.warning(
                'Сначала назначьте исполнителя — без него заявку нельзя перетаскивать'
            );
            return;
        }

        if (
            Array.isArray(allowed) &&
            !allowed.includes(statusName || null) &&
            !allowed.includes(statusName || '')
        ) {
            message.warning(
                'Этот переход недоступен по маршруту этапов'
            );
            return;
        }

        const prevStatus = task.status_name;

        setTasks(prev =>
            prev.map(item =>
                item.id === taskId
                    ? {
                          ...item,
                          status_name: statusName || null,
                          updated_at: new Date().toISOString()
                      }
                    : item
            )
        );

        setStatusAnimIds(prev => {
            const next = new Set(prev);
            next.add(taskId);
            return next;
        });
        window.setTimeout(() => {
            setStatusAnimIds(prev => {
                const next = new Set(prev);
                next.delete(taskId);
                return next;
            });
        }, 700);

        try {
            await tmApi.put(
                `/api/task-manager/tasks/${taskId}`,
                { status_name: statusName || null }
            );
            setTasks(prev =>
                prev.map(item => {
                    if (item.id !== taskId) return item;
                    const next = {
                        ...item,
                        status_name: statusName || null,
                        updated_at: new Date().toISOString()
                    };
                    const targets = getAllowedStatusTargets(
                        {
                            ...next,
                            workflow: undefined
                        },
                        workflowBundle
                    );
                    next.workflow = {
                        ...(item.workflow || {}),
                        from_status: statusName || null,
                        unrestricted: targets === null,
                        allowed_targets: targets
                    };
                    return next;
                })
            );
        } catch (error) {
            setTasks(prev =>
                prev.map(item =>
                    item.id === taskId
                        ? {
                              ...item,
                              status_name: prevStatus
                          }
                        : item
                )
            );
            message.error(
                error?.response?.data?.message ||
                    'Не удалось изменить статус'
            );
        }
    };

    const saveTask = async () => {
        try {
            const values = await taskForm.validateFields();
            const custom_fields = {};

            Object.keys(values).forEach(key => {
                if (key.startsWith('cf__')) {
                    const systemName = key.slice(4);
                    if (!READ_ONLY_CF.has(systemName)) {
                        custom_fields[systemName] =
                        values[key];
                    }
                }
            });

            const payload = {
                title: values.title,
                description: values.description,
                status: values.status,
                status_name: values.status_name,
                status_invoices: values.status_invoices,
                type: values.type,
                news_type_name: values.news_type_name,
                project_id: values.project_id || null,
                creator_org_id: values.creator_org_id || null,
                executor_org_id: values.executor_org_id || null,
                region_name: values.region_name,
                assigned_user_id: values.assigned_user_id,
                assigned_user_fio:
                    values.assigned_user_fio,
                custom_fields
            };

            if (editingTask) {
                await tmApi.put(
                    `/api/task-manager/tasks/${editingTask.id}`,
                    payload
                );
                message.success('Задача обновлена');
            } else {
                await tmApi.post(
                    '/api/task-manager/tasks',
                    payload
                );
                message.success('Задача создана');
            }

            setDetailOpen(false);
            loadTasks();
        } catch (error) {
            if (error?.errorFields) {
                return;
            }

            message.error(
                error?.response?.data?.message ||
                    'Ошибка сохранения'
            );
        }
    };

    const getCfPreview = useCallback((task, limit = 3) => {
        return (task.fields || [])
            .filter(
                item =>
                    item.field_kind === 'custom' &&
                    item.value != null &&
                    String(item.value) !== ''
            )
            .slice(0, limit);
    }, []);

    const renderFilterControl = fieldKey => {
        const standard = STANDARD_FILTER_FIELDS.find(
            item => item.key === fieldKey
        );

        if (fieldKey === 'status_name') {
            return (
                <Select
                    mode="multiple"
                    allowClear
                    size="small"
                    style={{ width: '100%' }}
                    placeholder="Статусы"
                    value={
                        draftFilters.status_name
                            ? String(
                                  draftFilters.status_name
                              )
                                  .split(',')
                                  .filter(Boolean)
                            : []
                    }
                    options={statuses.map(item => ({
                        value: item.name,
                        label: item.name
                    }))}
                    onChange={values =>
                        setDraftFilters(prev => ({
                            ...prev,
                            status_name: values.join(',')
                        }))
                    }
                />
            );
        }

        if (standard?.type === 'project') {
            return (
                <Select
                    allowClear
                    showSearch
                    size="small"
                    optionFilterProp="label"
                    style={{ width: '100%' }}
                    placeholder="Проект"
                    value={
                        draftFilters.project_id
                            ? Number(draftFilters.project_id)
                            : undefined
                    }
                    options={(orgBundle?.projects || []).map(
                        item => ({
                            value: item.id,
                            label: item.human_name
                        })
                    )}
                    onChange={value =>
                        setDraftFilters(prev => ({
                            ...prev,
                            project_id: value || ''
                        }))
                    }
                />
            );
        }

        if (standard?.type === 'org') {
            const orgs = (orgBundle?.organizations || []).filter(
                item => item.org_type === standard.orgType
            );
            return (
                <Select
                    allowClear
                    showSearch
                    size="small"
                    optionFilterProp="label"
                    style={{ width: '100%' }}
                    placeholder={
                        standard.orgType === 'creator'
                            ? 'Орг. создатель'
                            : 'Орг. исполнитель'
                    }
                    value={
                        draftFilters[fieldKey]
                            ? Number(draftFilters[fieldKey])
                            : undefined
                    }
                    options={orgs.map(item => ({
                        value: item.id,
                        label: item.human_name
                    }))}
                    onChange={value =>
                        setDraftFilters(prev => ({
                            ...prev,
                            [fieldKey]: value || ''
                        }))
                    }
                />
            );
        }

        if (standard?.type === 'select') {
            return (
                <Select
                    allowClear
                    size="small"
                    style={{ width: '100%' }}
                    value={
                        draftFilters[fieldKey] ||
                        undefined
                    }
                    options={(standard.options || []).map(
                        value => ({
                            value,
                            label: value
                        })
                    )}
                    onChange={value =>
                        setDraftFilters(prev => ({
                            ...prev,
                            [fieldKey]: value
                        }))
                    }
                />
            );
        }

        if (standard?.type === 'dictionary') {
            const meta = customFields.find(
                item => item.system_name === fieldKey
            );
            if (meta) {
                return (
                    <TmFieldValueInput
                        field={meta}
                        value={
                            draftFilters[fieldKey] ||
                            undefined
                        }
                        onChange={value =>
                            setDraftFilters(prev => ({
                                ...prev,
                                [fieldKey]: value
                            }))
                        }
                    />
                );
            }
        }

        const customMeta = fieldKey.startsWith('cf_')
            ? customFields.find(
                  item =>
                      `cf_${item.system_name}` ===
                      fieldKey
              )
            : null;

        if (
            customMeta?.value_type === 'select' &&
            (customMeta.possible_values || []).length
        ) {
            return (
                <Select
                    allowClear
                    size="small"
                    style={{ width: '100%' }}
                    mode={
                        customMeta.is_multiple
                            ? 'multiple'
                            : undefined
                    }
                    value={
                        draftFilters[fieldKey] ||
                        undefined
                    }
                    options={(
                        customMeta.possible_values ||
                        []
                    ).map(value => ({
                        value: String(value),
                        label: String(value)
                    }))}
                    onChange={value =>
                        setDraftFilters(prev => ({
                            ...prev,
                            [fieldKey]: value
                        }))
                    }
                />
            );
        }

        if (customMeta?.value_type === 'dictionary') {
            return (
                <TmFieldValueInput
                    field={customMeta}
                    value={
                        draftFilters[fieldKey] ||
                        undefined
                    }
                    onChange={value =>
                        setDraftFilters(prev => ({
                            ...prev,
                            [fieldKey]: value
                        }))
                    }
                />
            );
        }

        if (customMeta?.value_type === 'number') {
            return (
                <InputNumber
                    size="small"
                    style={{ width: '100%' }}
                    value={
                        draftFilters[fieldKey] != null &&
                        draftFilters[fieldKey] !== ''
                            ? Number(
                                  draftFilters[fieldKey]
                              )
                            : null
                    }
                    onChange={value =>
                        setDraftFilters(prev => ({
                            ...prev,
                            [fieldKey]:
                                value == null
                                    ? ''
                                    : String(value)
                        }))
                    }
                />
            );
        }

        return (
            <Input
                allowClear
                size="small"
                placeholder={
                    customMeta
                        ? `Фильтр: ${customMeta.human_name}`
                        : 'Значение'
                }
                value={draftFilters[fieldKey] || ''}
                onChange={e =>
                    setDraftFilters(prev => ({
                        ...prev,
                        [fieldKey]: e.target.value
                    }))
                }
                onPressEnter={() => applyFilters()}
            />
        );
    };

    const editableCustomFields = useMemo(() => {
        const byName = {};
        customFields.forEach(item => {
            byName[item.system_name] = item;
        });

        const source = editingTask?.fields
            ? (editingTask.fields || []).filter(
                item =>
                    item.field_kind === 'custom' &&
                      (item.can_edit ||
                          item.access_level === 'read' ||
                          item.access_level === 'full')
              )
            : customFields.filter(
                  item => item.field_kind === 'custom'
              );

        return source.map(item => ({
            ...byName[item.system_name],
            ...item
        }));
    }, [editingTask, customFields]);

    const mainEditableFields = useMemo(
        () =>
            editableCustomFields.filter(
                field =>
                    !READ_ONLY_CF.has(field.system_name) &&
                    field.system_name !== TMC_CF
            ),
        [editableCustomFields]
    );

    const mainFieldsByGroup = useMemo(() => {
        const map = new Map();
        for (const field of mainEditableFields) {
            const key =
                field.group?.id ||
                field.group_id ||
                'ungrouped';
            const title =
                field.group?.human_name ||
                (key === 'ungrouped'
                    ? 'Дополнительно'
                    : 'Группа');
            if (!map.has(key)) {
                map.set(key, {
                    id: key,
                    title,
                    sort:
                        field.group?.sort_order ??
                        9999,
                    fields: []
                });
            }
            map.get(key).fields.push(field);
        }
        return Array.from(map.values()).sort(
            (a, b) => a.sort - b.sort
        );
    }, [mainEditableFields]);

    const workTypeField = useMemo(() => {
        const fromEdit = (editingTask?.fields || []).find(
            item => item.system_name === 'news_type_name'
        );
        const fromMeta = customFields.find(
            item => item.system_name === 'news_type_name'
        );
        return fromEdit || fromMeta || null;
    }, [editingTask, customFields]);

    const tmcField = useMemo(() => {
        const fromEdit = (editingTask?.fields || []).find(
            item => item.system_name === TMC_CF
        );
        const fromMeta = customFields.find(
            item => item.system_name === TMC_CF
        );
        return fromEdit || fromMeta || null;
    }, [editingTask, customFields]);

    const customerLabel = useMemo(() => {
        const raw =
            editingTask?.region_name ||
            cfValue(editingTask, 'Zakazchik');
        return resolveCustomerLabel(raw, customers);
    }, [editingTask, customers]);

    const filterPanel = (
        <div className="tm-filter">
            <div className="tm-filter-header">
                <div className="tm-filter-header-text">
                    <span className="tm-filter-kicker">Task Manager</span>
                    <Text className="tm-filter-title">Фильтр</Text>
                </div>
                <Button
                    type="text"
                    size="small"
                    className="tm-filter-collapse"
                    icon={<MenuFoldOutlined />}
                    onClick={() => {
                        if (isMobile) {
                            setMobileFilterOpen(false);
                            return;
                        }
                        toggleFilterVisible(false);
                    }}
                />
            </div>

            <div className="tm-filter-body">
                <div className="tm-filter-templates">
                    <Text className="tm-filter-hint">
                        Шаблоны фильтра
                    </Text>
                    <div className="tm-filter-templates-row">
                        <Select
                            className="tm-filter-template-select"
                            size="small"
                            allowClear
                            placeholder="Выбрать шаблон"
                            value={
                                activeFilterTemplateId ||
                                undefined
                            }
                            options={filterTemplates.map(
                                item => ({
                                    value: item.id,
                                    label: item.name
                                })
                            )}
                            onChange={value => {
                                if (!value) {
                                    setActiveFilterTemplateId(
                                        null
                                    );
                                    return;
                                }
                                applyFilterTemplate(value);
                            }}
                        />
                        <Button
                            size="small"
                            type="primary"
                            icon={<SaveOutlined />}
                            title="Сохранить текущий фильтр как шаблон"
                            onClick={() => {
                                setTemplateName('');
                                setTemplateModalOpen(true);
                            }}
                        />
                        <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            title="Удалить выбранный шаблон"
                            disabled={!activeFilterTemplateId}
                            onClick={() =>
                                deleteFilterTemplate()
                            }
                        />
                    </div>
                </div>

                <Text className="tm-filter-hint">
                    Поля фильтра
                </Text>
                <Select
                    className="tm-filter-picker"
                    mode="multiple"
                    allowClear
                    showSearch
                    size="small"
                    optionFilterProp="label"
                    placeholder="Добавить поле"
                    value={draftFilterFields}
                    options={availableFilterOptions}
                    onChange={setDraftFilterFields}
                />

                <Divider className="tm-filter-divider" />

                {(draftFilterFields || []).length ===
                0 ? (
                    <Empty
                        className="tm-filter-empty"
                        image={
                            Empty.PRESENTED_IMAGE_SIMPLE
                        }
                        description="Добавьте поля фильтра сверху"
                    />
                ) : (
                    draftFilterFields.map(fieldKey => {
                        const label =
                            availableFilterOptions.find(
                                item =>
                                    item.value ===
                                    fieldKey
                            )?.label || fieldKey;

                        const isCustom =
                            fieldKey.startsWith('cf_');

                        return (
                            <div
                                key={fieldKey}
                                className="tm-filter-field"
                            >
                                <div className="tm-filter-field-label">
                                    <Text type="secondary">
                                        {label}
                                    </Text>
                                    {isCustom ? (
                                        <Tag className="tm-filter-field-tag">
                                            кастом
                                        </Tag>
                                    ) : null}
                                </div>
                                <div className="tm-filter-field-control">
                                    {renderFilterControl(
                                        fieldKey
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            <div className="tm-filter-footer">
                <Button block onClick={resetFilters}>
                    Сбросить
                </Button>
                <Button
                    block
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={() => applyFilters()}
                >
                    Применить
                </Button>
            </div>
        </div>
    );

    const useDetailDrawer = detailInDrawer || isMobile;
    const splitDetail = detailOpen && !useDetailDrawer;

    const activeFilterCount = useMemo(
        () => Object.keys(cleanFilters(draftFilters)).length,
        [draftFilters]
    );

    const isBooleanField = field =>
        (field?.value_type || '') === 'boolean';

    const isFullWidthField = field =>
        (field?.value_type || 'string') === 'text';

    const fieldGridClass = field => {
        if (isFullWidthField(field)) {
            return 'tm-detail-field--full';
        }
        if (isBooleanField(field)) {
            return 'tm-detail-field--switch';
        }
        if (field?.value_type === 'number') {
            return 'tm-detail-field--compact';
        }
        if (
            field?.value_type === 'date' ||
            field?.value_type === 'datetime'
        ) {
            return 'tm-detail-field--medium';
        }
        const labelLen = String(
            field?.human_name || field?.system_name || ''
        ).length;
        if (labelLen > 24) {
            return 'tm-detail-field--medium';
        }
        return 'tm-detail-field--default';
    };

    const renderReadonlyFieldGrid = items =>
        items.length ? (
            <div className="tm-detail-readonly-grid">
                {items.map(item => (
                    <div
                        key={item.key}
                        className={`tm-detail-readonly-item${
                            item.wide
                                ? ' tm-detail-readonly-item--wide'
                                : ''
                        }`}
                    >
                        <span className="tm-detail-meta-label">
                            {item.label}
                        </span>
                        <span className="tm-detail-meta-value">
                            {item.value ?? '—'}
                        </span>
                    </div>
                ))}
                </div>
        ) : null;

    const taskDetailTitle = (
                <div
                    style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: '6px 14px',
                            minWidth: 0,
                            paddingRight: 8
                        }}
                    >
                        <StatusTag
                            status={
                                watchedStatus ||
                                editingTask?.status_name
                            }
                            colorMap={colorMap}
                        />
                        {editingTask ? (
                            <Text type="secondary">
                                ТТ{' '}
                                <Text strong>
                                    {cfValue(
                                        editingTask,
                                        'Nomer_TT'
                                    ) ||
                                        editingTask.task_number ||
                                        '—'}
                                </Text>
                            </Text>
                        ) : (
                            <Text strong>Новая задача</Text>
                        )}
                        <Text
                            strong
                        style={{
                                maxWidth: 420,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                            title={
                                watchedTitle ||
                                editingTask?.title ||
                                ''
                            }
                        >
                            {watchedTitle ||
                                editingTask?.title ||
                                'Без названия'}
                        </Text>
                                <Text type="secondary">
                            {watchedAssignee ||
                                editingTask?.assigned_user_fio ||
                                'Исполнитель не назначен'}
                                </Text>
                        {editingTask ? (
                            <Text type="secondary">
                                Создана{' '}
                                {formatMoscowDateTime(
                                    editingTask.created_at
                                )}
                            </Text>
                        ) : null}
                            </div>
    );

    const taskDetailActions = (
                        <Space wrap>
                        {editingTask?.id ? (
                            <Button
                                icon={<MessageOutlined />}
                                loading={dispatcherChatLoading}
                                onClick={openDispatcherChat}
                            >
                                Написать диспетчеру
                            </Button>
                        ) : null}
                        {editingTask?.org_permissions
                            ?.can_take ? (
                            <Button onClick={takeTask}>
                                Взять заявку
                            </Button>
                        ) : null}
                            <Button
                                type="primary"
                            onClick={saveTask}
                                disabled={
                                editingTask
                                    ? !taskAccess?.can_edit
                                    : !taskAccess?.can_create
                                }
                            >
                            Сохранить
                            </Button>
                        </Space>
    );

    const taskDetailForm = (
                <Form
                    form={taskForm}
                    layout="vertical"
                    size="small"
                    className={
                        splitDetail
                            ? 'tm-task-detail tm-task-detail--split'
                            : 'tm-task-detail'
                    }
                >
                <Row gutter={12}>
                    <Col xs={24} lg={splitDetail ? 24 : 14}>
                            <Form.Item name="title" hidden>
                                <Input />
                            </Form.Item>
                            <Form.Item
                                name="assigned_user_id"
                                hidden
                            >
                                <Input />
                            </Form.Item>
                            <Form.Item
                                name="cf__Nomer_TT"
                                hidden
                            >
                                <Input />
                            </Form.Item>
                            <Form.Item
                                name="cf__Nomer_BS"
                                hidden
                            >
                                <Input />
                            </Form.Item>
                            <Form.Item
                                name="cf__Zakazchik"
                                hidden
                            >
                                <Input />
                            </Form.Item>
                            <Form.Item
                                name="cf__Adres_BS"
                                hidden
                            >
                                <Input />
                            </Form.Item>
                            <Form.Item
                                name="region_name"
                                hidden
                            >
                                <Input />
                            </Form.Item>

                            <Tabs
                                size="small"
                                activeKey={detailTab}
                                onChange={key => {
                                    setDetailTab(key);
                                    if (
                                        key === 'report' &&
                                        editingTask?.id
                                    ) {
                                        loadReportBundle(
                                            editingTask.id
                                        );
                                    }
                                    if (
                                        key === 'mail' &&
                                        editingTask?.id
                                    ) {
                                        loadMailHistory(
                                            editingTask.id,
                                            { refresh: true }
                                        );
                                    }
                                }}
                                items={[
                                    {
                                        key: 'main',
                                        label: 'Основное',
                                        children: (
                                            <>
                                                {editingTask ? (
                                                    <div className="tm-detail-meta">
                                                        <div className="tm-detail-meta-item">
                                                            <span className="tm-detail-meta-label">
                                                                Номер БС
                                                            </span>
                                                            <span className="tm-detail-meta-value">
                                                                {cfValue(
                                                                    editingTask,
                                                                    'Nomer_BS'
                                                                ) ||
                                                                    '—'}
                                                            </span>
                                                        </div>
                                                        <div className="tm-detail-meta-item">
                                                            <span className="tm-detail-meta-label">
                                                                Дата заявки
                                                            </span>
                                                            <span className="tm-detail-meta-value">
                                                                {formatMoscowDateTime(
                                                                    editingTask.news_date
                                                                )}
                                                            </span>
                                                        </div>
                                                        <div className="tm-detail-meta-item tm-detail-meta-item--wide">
                                                            <span className="tm-detail-meta-label">
                                                                Заказчик
                                                            </span>
                                                            <span className="tm-detail-meta-value">
                                                                {
                                                                    customerLabel
                                                                }
                                                            </span>
                                                        </div>
                                                        <div className="tm-detail-meta-item tm-detail-meta-item--wide">
                                                            <span className="tm-detail-meta-label">
                                                                Адрес БС
                                                            </span>
                                                            <span className="tm-detail-meta-value">
                                                                {cfValue(
                                                                    editingTask,
                                                                    'Adres_BS'
                                                                ) ||
                                                                    '—'}
                                                                {mapUrl() ? (
                                                                    <>
                                                                        {' '}
                                                                        <a
                                                                            href={
                                                                                mapUrl()
                                                                            }
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                        >
                                                                            <EnvironmentOutlined />{' '}
                                                                            Карта
                                                                        </a>
                                                                    </>
                                                                ) : null}
                                                            </span>
                                                        </div>
                                                        <div className="tm-detail-meta-item">
                                                            <span className="tm-detail-meta-label">
                                                                Расстояние до БС
                                                            </span>
                                                            <span className="tm-detail-meta-value">
                                                                {formatDistanceKm(
                                                                    cfValue(
                                                                        editingTask,
                                                                        'Rasstojanie_do_BS'
                                                                    )
                                                                )}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ) : null}

                                                <div className="tm-detail-field--full">
                                                {mainFieldVisible(
                                                    'description'
                                                ) ? (
                                                    <Form.Item
                                                        name="description"
                                                        label="Описание"
                                                    >
                                                        <TextArea
                                                            rows={2}
                                                            disabled={mainFieldReadonly(
                                                                'description'
                                                            )}
                                                        />
                                                    </Form.Item>
                                                ) : null}
                                                </div>

                                                <div className="tm-detail-fields-row">
                                                    {mainFieldVisible(
                                                        'status_name'
                                                    ) ? (
                                                            <Form.Item
                                                                name="status_name"
                                                                label="Статус / этап"
                                                                extra={
                                                                    editingTask
                                                                        ?.workflow
                                                                        ?.funnel
                                                                        ?.human_name
                                                                        ? `Маршрут: ${editingTask.workflow.funnel.human_name}`
                                                                        : undefined
                                                                }
                                                            >
                                                                <Select
                                                                    options={
                                                                        statusSelectOptions
                                                                    }
                                                                    disabled={mainFieldReadonly(
                                                                        'status_name'
                                                                    )}
                                                                />
                                                            </Form.Item>
                                                    ) : null}
                                                    {mainFieldVisible(
                                                        'status_invoices'
                                                    ) ? (
                                                            <Form.Item
                                                                name="status_invoices"
                                                                label="Статус ВДР"
                                                            >
                                                                <Select
                                                                    disabled={mainFieldReadonly(
                                                                        'status_invoices'
                                                                    )}
                                                                    options={[
                                                                        'new',
                                                                        'in_progress',
                                                                        'filled',
                                                                        'rework',
                                                                        'approval',
                                                                        'approved',
                                                                        'analysis'
                                                                    ].map(
                                                                        value => ({
                                                                            value,
                                                                            label: value
                                                                        })
                                                                    )}
                                                                />
                                                            </Form.Item>
                                                    ) : null}
                                                    {mainFieldVisible(
                                                        'news_type_name'
                                                    ) ? (
                                                    <Form.Item
                                                        name="news_type_name"
                                                        label={
                                                            workTypeField?.human_name ||
                                                            'Тип работ'
                                                        }
                                                    >
                                                        {workTypeField?.value_type ===
                                                        'dictionary' ? (
                                                            <TmFieldValueInput
                                                                field={
                                                                    workTypeField
                                                                }
                                                                disabled={mainFieldReadonly(
                                                                    'news_type_name'
                                                                )}
                                                            />
                                                        ) : (
                                                            <Input
                                                                disabled={mainFieldReadonly(
                                                                    'news_type_name'
                                                                )}
                                                            />
                                                        )}
                                                    </Form.Item>
                                                    ) : null}
                                                </div>

                                                <div className="tm-detail-fields-row">
                                                        <Form.Item
                                                            name="project_id"
                                                            label="Проект"
                                                        >
                                                            <Select
                                                                allowClear
                                                                showSearch
                                                                optionFilterProp="label"
                                                                options={
                                                                    projectOptions
                                                                }
                                                                placeholder="Проект"
                                                            />
                                                        </Form.Item>
                                                        <Form.Item
                                                            name="creator_org_id"
                                                            label="Орг. создатель"
                                                        >
                                                            <Select
                                                                allowClear
                                                                showSearch
                                                                optionFilterProp="label"
                                                                options={
                                                                    creatorOrgOptions
                                                                }
                                                                placeholder="Москва СВ…"
                                                            />
                                                        </Form.Item>
                                                        <Form.Item
                                                            name="executor_org_id"
                                                            label="Орг. исполнитель"
                                                        >
                                                            <Select
                                                                allowClear
                                                                showSearch
                                                                optionFilterProp="label"
                                                                options={
                                                                    executorOrgOptions
                                                                }
                                                                placeholder="ИП…"
                                                            />
                                                        </Form.Item>
                                                    {mainFieldVisible(
                                                        'assigned_user_fio'
                                                    ) ? (
                                                    <Form.Item
                                                        name="assigned_user_fio"
                                                        label="Исполнитель"
                                                    >
                                                        <Select
                                                            showSearch
                                                            allowClear
                                                            disabled={mainFieldReadonly(
                                                                'assigned_user_fio'
                                                            )}
                                                            optionFilterProp="label"
                                                            options={installers.map(
                                                                item => ({
                                                                    value:
                                                                        item.value ||
                                                                        item.label,
                                                                    label:
                                                                        item.label ||
                                                                        item.value
                                                                })
                                                            )}
                                                            onChange={
                                                                onInstallerChange
                                                            }
                                                            placeholder="Монтажник"
                                                        />
                                                    </Form.Item>
                                                    ) : null}
                                                </div>

                                                {mainFieldsByGroup.map(
                                                    group => (
                                                        <div
                                                            key={
                                                                group.id
                                                            }
                                                            className="tm-detail-group tm-detail-group-panel"
                                                        >
                                                            <div className="tm-detail-group-title">
                                                                {
                                                                    group.title
                                                                }
                                                            </div>
                                                            {group.fields.some(
                                                                isBooleanField
                                                            ) ? (
                                                                <div className="tm-detail-switches-bar">
                                                                    {group.fields
                                                                        .filter(
                                                                            isBooleanField
                                                                        )
                                                                        .map(
                                                                            field => (
                                                                                <Form.Item
                                                                                    key={
                                                                                        field.system_name
                                                                                    }
                                                                                    className="tm-detail-field--switch"
                                                                                    name={`cf__${field.system_name}`}
                                                                                    label={
                                                                                        field.human_name ||
                                                                                        field.system_name
                                                                                    }
                                                                                    valuePropName="checked"
                                                                                >
                                                                                    <TmFieldValueInput
                                                                                        field={
                                                                                            field
                                                                                        }
                                                                                        disabled={
                                                                                            editingTask
                                                                                                ? !field.can_edit
                                                                                                : false
                                                                                        }
                                                                                    />
                                                                                </Form.Item>
                                                                            )
                                                                        )}
                                                                </div>
                                                            ) : null}
                                                            <div className="tm-detail-fields-row">
                                                                {group.fields
                                                                    .filter(
                                                                        field =>
                                                                            !isBooleanField(
                                                                                field
                                                                            )
                                                                    )
                                                                    .map(
                                                                        field => (
                                                                            <div
                                                                                key={
                                                                                    field.system_name
                                                                                }
                                                                                className={`tm-detail-field-cell ${fieldGridClass(field)}`}
                                                                            >
                                                                            <Form.Item
                                                                                name={`cf__${field.system_name}`}
                                                                                label={
                                                                                    field.human_name ||
                                                                                    field.system_name
                                                                                }
                                                                            >
                                                                                <TmFieldValueInput
                                                                                    field={
                                                                                        field
                                                                                    }
                                                                                    disabled={
                                                                                        editingTask
                                                                                            ? !field.can_edit
                                                                                            : false
                                                                                    }
                                                                                />
                                                                            </Form.Item>
                                                                            </div>
                                                                        )
                                                                    )}
                                                            </div>
                                                        </div>
                                                    )
                                                )}
                                            </>
                                        )
                                    },
                                    {
                                        key: 'bs',
                                        label: 'История по БС',
                                        disabled: !editingTask,
                                        children: (
                                            <div className="tm-bs-history">
                                            <Card
                                                size="small"
                                                    className="tm-bs-history-card"
                                                    title={
                                                        bsHistory?.bs_number
                                                            ? `БС ${bsHistory.bs_number}`
                                                            : 'История работ по БС'
                                                    }
                                                >
                                                    {(bsHistory?.photos ||
                                                        []).length ? (
                                                        <Text type="secondary">
                                                            Фото общего
                                                            ракурса:{' '}
                                                            {
                                                                bsHistory
                                                                    .photos
                                                                    .length
                                                            }
                                                        </Text>
                                                    ) : (
                                                        <Text type="secondary">
                                                            Фото общего
                                                            ракурса БС
                                                            пока нет
                                                        </Text>
                                                    )}
                                                    <Divider
                                                        style={{
                                                            margin:
                                                                '12px 0'
                                                        }}
                                                    />
                                                    {(bsHistory?.logs ||
                                                        []).length ? (
                                                        <div className="tm-bs-history-list">
                                                            {bsHistory.logs.map(
                                                                log => {
                                                                    const isCurrent =
                                                                        log.source ===
                                                                            'tm_task' &&
                                                                        Number(
                                                                            log.source_id
                                                                        ) ===
                                                                            Number(
                                                                                editingTask?.id
                                                                            );
                                                                    const isSelected =
                                                                        bsHistorySelectedLog?.id ===
                                                                        log.id;
                                                                    return (
                                                                        <button
                                                                            key={
                                                                                log.id
                                                                            }
                                                                            type="button"
                                                                            className={`tm-bs-history-item${
                                                                                isSelected
                                                                                    ? ' tm-bs-history-item--selected'
                                                                                    : ''
                                                                            }`}
                                                onClick={() =>
                                                                                openBsHistoryPreview(
                                                                                    log
                                                                                )
                                                                            }
                                                                        >
                                                                            <div className="tm-bs-history-item-main">
                                                                                <Text strong>
                                                                                    {log.task_number ||
                                                                                        '—'}
                                                                                </Text>
                                                                                {log.news_date ? (
                                                                                    <Text type="secondary">
                                                                                        {' '}
                                                                                        ·{' '}
                                                                                        {formatMoscowDateTime(
                                                                                            log.news_date,
                                                                                            {
                                                                                                dateOnly: true
                                                                                            }
                                                                                        )}
                                                                                    </Text>
                                                                                ) : null}
                                                                                {isCurrent ? (
                                                                                    <Tag
                                                                                        color="blue"
                                                                                        style={{
                                                                                            marginLeft: 8
                                                                                        }}
                                                                                    >
                                                                                        текущая
                                                                                    </Tag>
                                                                                ) : null}
                                                                                <div>
                                                                                    {log.title ||
                                                                                        ''}
                                                                                </div>
                                                                                {log.work_summary ? (
                                                                                    <Text type="secondary">
                                                                                        {
                                                                                            log.work_summary
                                                                                        }
                                                                                    </Text>
                                                                                ) : null}
                                                                            </div>
                                                                            <EyeOutlined className="tm-bs-history-item-icon" />
                                                                        </button>
                                                                    );
                                                                }
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <Empty
                                                            description="Нет истории по этой БС"
                                                            image={
                                                                Empty.PRESENTED_IMAGE_SIMPLE
                                                            }
                                                        />
                                                    )}
                                                </Card>
                                                {bsHistorySelectedLog ? (
                                                    <Card
                                                        size="small"
                                                        className="tm-bs-history-preview"
                                                        title={
                                                            <Space>
                                                                <span>
                                                                    Просмотр
                                                                    заявки
                                                                </span>
                                                                <Text type="secondary">
                                                                    {bsHistorySelectedLog.task_number ||
                                                                        '—'}
                                                                </Text>
                                                            </Space>
                                                        }
                                                        extra={
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                icon={
                                                                    <CloseOutlined />
                                                                }
                                                                onClick={() => {
                                                                    setBsHistorySelectedLog(
                                                                        null
                                                                    );
                                                                    setBsHistoryPreview(
                                                                        null
                                                                    );
                                                                }}
                                                            />
                                                        }
                                                    >
                                                        {renderBsHistoryPreview()}
                                                    </Card>
                                                ) : null}
                                            </div>
                                        )
                                    },
                                    {
                                        key: 'mail',
                                        label: (
                                            <Space size={4}>
                                                <MailOutlined />
                                                Почта
                                            </Space>
                                        ),
                                        disabled: !editingTask,
                                        children: (
                                            <div className="tm-mail-history">
                                                <div className="tm-mail-history-toolbar">
                                                    <Text type="secondary">
                                                        {mailHistory?.wo
                                                            ? `WO: ${mailHistory.wo}`
                                                            : 'Письма по номеру заявки'}
                                                        {mailHistory?.refresh_error
                                                            ? ` · IMAP: ${mailHistory.refresh_error}`
                                                            : ''}
                                                    </Text>
                                                    <Space wrap>
                                                        <Button
                                                            size="small"
                                                            icon={
                                                                <SendOutlined />
                                                            }
                                                            onClick={() =>
                                                                openMailCompose(
                                                                    'new'
                                                                )
                                                            }
                                                        >
                                                            Написать
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            icon={
                                                                <ReloadOutlined />
                                                            }
                                                            loading={
                                                                mailHistoryLoading
                                                            }
                                                            onClick={() =>
                                                                loadMailHistory(
                                                                    editingTask?.id,
                                                                    {
                                                                        refresh: true
                                                                    }
                                                                )
                                                            }
                                                        >
                                                            Обновить из почты
                                                        </Button>
                                                    </Space>
                                                </div>
                                                {mailHistoryLoading &&
                                                !(
                                                    mailHistory
                                                        ?.items || []
                                                ).length ? (
                                                    <div className="tm-mail-history-loading">
                                                        <Spin />
                                                    </div>
                                                ) : (
                                                        mailHistory
                                                            ?.items || []
                                                    ).length ? (
                                                    <Timeline
                                                        className="tm-mail-history-timeline"
                                                        items={(
                                                            mailHistory.items ||
                                                            []
                                                        ).map(mail => ({
                                                            color: mail.status_label
                                                                ?.toLowerCase()
                                                                .includes(
                                                                    'нов'
                                                                )
                                                                ? 'blue'
                                                                : mail.status_label
                                                                        ?.toLowerCase()
                                                                        .includes(
                                                                            'закр'
                                                                        )
                                                                  ? 'green'
                                                                  : 'gray',
                                                            children: (
                                                                <Card
                                                                    size="small"
                                                                    className="tm-mail-history-card"
                                                                    title={
                                                                        <Space wrap>
                                                                            <span>
                                                                                {mail.subject ||
                                                                                    'Без темы'}
                                                                            </span>
                                                                            {mail.status_label ? (
                                                                                <Tag>
                                                                                    {
                                                                                        mail.status_label
                                                                                    }
                                                                                </Tag>
                                                                            ) : null}
                                                                        </Space>
                                                                    }
                                                                    extra={
                                                                        <Button
                                                                            size="small"
                                                                            type="link"
                                                                            icon={
                                                                                <RollbackOutlined />
                                                                            }
                                                                            onClick={() =>
                                                                                openMailCompose(
                                                                                    'reply',
                                                                                    mail
                                                                                )
                                                                            }
                                                                        >
                                                                            Ответить
                                                                        </Button>
                                                                    }
                                                                >
                                                                    <Descriptions
                                                                        size="small"
                                                                        column={1}
                                                                    >
                                                                        <Descriptions.Item label="Дата">
                                                                            {formatMoscowDateTime(
                                                                                mail.received_at
                                                                            ) ||
                                                                                '—'}
                                                                        </Descriptions.Item>
                                                                        <Descriptions.Item label="От">
                                                                            {mail.from_email ||
                                                                                '—'}
                                                                        </Descriptions.Item>
                                                                        {mail.to_email ? (
                                                                            <Descriptions.Item label="Кому">
                                                                                {
                                                                                    mail.to_email
                                                                                }
                                                                            </Descriptions.Item>
                                                                        ) : null}
                                                                        {mail.cc_email ? (
                                                                            <Descriptions.Item label="Копия">
                                                                                {
                                                                                    mail.cc_email
                                                                                }
                                                                            </Descriptions.Item>
                                                                        ) : null}
                                                                        {mail.bs ? (
                                                                            <Descriptions.Item label="БС">
                                                                                {
                                                                                    mail.bs
                                                                                }
                                                                            </Descriptions.Item>
                                                                        ) : null}
                                                                    </Descriptions>
                                                                    <Divider
                                                                        style={{
                                                                            margin:
                                                                                '8px 0'
                                                                        }}
                                                                    />
                                                                    <Collapse
                                                                        ghost
                                                                        size="small"
                                                                        className="tm-mail-history-collapse"
                                                                        items={[
                                                                            {
                                                                                key: 'body',
                                                                                label: 'Полный текст письма',
                                                                                children: (
                                                                                    <pre className="tm-mail-history-body">
                                                                                        {mail.text_body ||
                                                                                            (mail.html_body
                                                                                                ? String(
                                                                                                      mail.html_body
                                                                                                  ).replace(
                                                                                                      /<[^>]+>/g,
                                                                                                      ' '
                                                                                                  )
                                                                                                : 'Нет текста письма')}
                                                                                    </pre>
                                                                                )
                                                                            }
                                                                        ]}
                                                                    />
                                                                </Card>
                                                            )
                                                        }))}
                                                    />
                                                ) : (
                                                    <Empty
                                                        description="Писем по этой заявке пока нет"
                                                        image={
                                                            Empty.PRESENTED_IMAGE_SIMPLE
                                                        }
                                                    />
                                                )}
                                            </div>
                                        )
                                    },
                                    {
                                        key: 'tmc',
                                        label: 'ТМЦ',
                                        disabled: !tmcField,
                                        children: tmcField ? (
                                            <Form.Item
                                                name={`cf__${TMC_CF}`}
                                                label={
                                                    tmcField.human_name ||
                                                    'ТМЦ'
                                                }
                                            >
                                                <TmFieldValueInput
                                                    field={tmcField}
                                                    disabled={
                                                        !tmcField.can_edit
                                                    }
                                                />
                                            </Form.Item>
                                        ) : (
                                            <Empty description="Поле ТМЦ не настроено" />
                                        )
                                    },
                                    {
                                        key: 'report',
                                        label: 'Отчёт',
                                        disabled: !editingTask,
                                        children: (
                                                <Space
                                                    direction="vertical"
                                                size={16}
                                                    style={{
                                                        width: '100%'
                                                    }}
                                                >
                                                {(reportBundle?.reports ||
                                                    []).length ? (
                                                    reportBundle.reports.map(
                                                        report => (
                                                            <Card
                                                                key={
                                                                    report.id
                                                                }
                                                                size="small"
                                                                title={`Отчёт монтажника${report.user_name ? `: ${report.user_name}` : ''}`}
                                                            >
                                                                <Descriptions
                                                                    column={1}
                                                                    size="small"
                                                                >
                                                                    <Descriptions.Item label="Статус">
                                                                        {
                                                                            report.status
                                                                        }
                                                                    </Descriptions.Item>
                                                                    <Descriptions.Item label="Сдан">
                                                                        {formatMoscowDateTime(
                                                                            report.submitted_at
                                                                        )}
                                                                    </Descriptions.Item>
                                                                    <Descriptions.Item label="Описание">
                                                                        {report.description ||
                                                                            '—'}
                                                                    </Descriptions.Item>
                                                                </Descriptions>
                                                                {(report.tmc_items ||
                                                                    []).length ? (
                                                                    <div
                                                        style={{
                                                                            marginTop: 8
                                                                        }}
                                                                    >
                                                                        <Text
                                                                            type="secondary"
                                                                        >
                                                                            ТМЦ в
                                                                            отчёте:{' '}
                                                                            {
                                                                                report
                                                                                    .tmc_items
                                                                                    .length
                                                            }
                                                        </Text>
                                                                    </div>
                                                                ) : null}
                                                            </Card>
                                                        )
                                                    )
                                                ) : (
                                                    <Empty description="Отчёт монтажника ещё не заполнен" />
                                                )}

                                                {(reportBundle?.photos ||
                                                    []).length ? (
                                                    <Card
                                                        size="small"
                                                        title="Фото отчёта"
                                                    >
                                                        <Space
                                                            wrap
                                                            size={8}
                                                        >
                                                            {reportBundle.photos.map(
                                                                photo => {
                                                                    const src =
                                                                        photoPreviewUrl(
                                                                            photo.path
                                                                        );
                                                                    return (
                                                                        <div
                                                                            key={
                                                                                photo.id
                                                                            }
                                                                            style={{
                                                                                width: 120
                                                                            }}
                                                                        >
                                                                            {src ? (
                                                                                <a
                                                                                    href={
                                                                                        src
                                                                                    }
                                                                                    target="_blank"
                                                                                    rel="noreferrer"
                                                                                >
                                                                                    <img
                                                                                        src={
                                                                                            src
                                                                                        }
                                                                                        alt={
                                                                                            photo.marker_label ||
                                                                                            photo.marker
                                                                                        }
                                                                                        style={{
                                                                                            width: '100%',
                                                                                            height: 90,
                                                                                            objectFit:
                                                                                                'cover',
                                                                                            borderRadius: 6,
                                                                                            border: '1px solid #f0f0f0'
                                                                                        }}
                                                                                    />
                                                                                </a>
                                                                            ) : null}
                                                    <Text
                                                                                type="secondary"
                                                        style={{
                                                                                    fontSize: 11,
                                                                                    display:
                                                                                        'block'
                                                        }}
                                                    >
                                                                                {photo.marker_label ||
                                                                                    photo.marker}
                                                    </Text>
                                                                        </div>
                                                                    );
                                                                }
                                                            )}
                                                        </Space>
                                                    </Card>
                                                ) : null}
                                            </Space>
                                        )
                                    }
                                ]}
                            />
                    </Col>
                    <Col xs={24} lg={splitDetail ? 24 : 10}>
                        <Card
                            size="small"
                            className="tm-comments-card"
                            title="Комментарии и история"
                            extra={
                                editingTask?.id ? (
                                    <Button
                                        size="small"
                                        type="link"
                                        icon={<MessageOutlined />}
                                        loading={
                                            dispatcherChatLoading
                                        }
                                        onClick={openDispatcherChat}
                                    >
                                        Чат с диспетчером
                                    </Button>
                                ) : null
                            }
                        >
                            <div
                                className="tm-task-timeline"
                                ref={el => {
                                    if (
                                        el &&
                                        timeline?.length
                                    ) {
                                        el.scrollTop =
                                            el.scrollHeight;
                                    }
                                                            }}
                                                            style={{
                                    overflowY: 'auto',
                                    paddingRight: 4,
                                    marginBottom: 8
                                }}
                            >
                            <Timeline
                                items={(timeline || []).map(
                                    item => ({
                                        color:
                                            item.kind ===
                                            'comment'
                                                ? item.source ===
                                                  'chat'
                                                    ? 'cyan'
                                                    : 'blue'
                                                : 'gray',
                                        children: (
                                            <div>
                                                <Text
                                                        type="secondary"
                                                        style={{
                                                        fontSize: 12
                                                    }}
                                                >
                                                    {item.user_name}{' '}
                                                    ·{' '}
                                                    {item.created_at
                                                        ? formatMoscowDateTime(
                                                              item.created_at
                                                          )
                                                        : ''}
                                                    {item.source ===
                                                    'chat' ? (
                                                        <>
                                                            {' '}
                                                            <Tag
                                                                color="cyan"
                                                                style={{
                                                                    marginInlineStart: 4,
                                                                    fontSize: 11,
                                                                    lineHeight: '16px',
                                                                    padding: '0 6px'
                                                                }}
                                                            >
                                                                чат
                                                            </Tag>
                                                        </>
                                                    ) : null}
                                                </Text>
                                                {item.kind ===
                                                'comment' ? (
                                                    <div
                                                        style={{
                                                            whiteSpace:
                                                                'pre-wrap'
                                                        }}
                                                    >
                                                        {
                                                            item.text
                                                        }
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <Text strong>
                                                            {
                                                                item.field_label
                                                            }
                                                        </Text>
                                                                        :{' '}
                                                        <Text
                                                            delete
                                                            type="secondary"
                                                        >
                                                            {item.old_value ||
                                                                '—'}
                                                        </Text>{' '}
                                                        →{' '}
                                                        {item.new_value ||
                                                            '—'}
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    })
                                )}
                            />
                            {!timeline.length ? (
                                <Text type="secondary">
                                    Пока нет записей
                                </Text>
                                                    ) : null}
                            </div>
                            {editingTask ? (
                                <Space
                                    direction="vertical"
                                    style={{
                                        width: '100%',
                                        marginTop: 12
                                    }}
                                >
                                    <TextArea
                                        rows={3}
                                        value={commentText}
                                        onChange={e =>
                                            setCommentText(
                                                e.target.value
                                            )
                                        }
                                        placeholder="Комментарий"
                                    />
                                    <Button
                                        type="primary"
                                        loading={commentSending}
                                        onClick={sendComment}
                                    >
                                        Добавить комментарий
                                    </Button>
                                                </Space>
                            ) : (
                                <Text type="secondary">
                                    Сохраните задачу, чтобы писать
                                    комментарии
                                </Text>
                            )}
                                            </Card>
                                        </Col>
                            </Row>
                </Form>
    );

    return (
        <Layout fullHeight>
                    <div
                className={
                    isMobile
                        ? 'tm-page-shell tm-page-shell--mobile'
                        : 'tm-page-shell'
                }
                        style={{
                            display: 'flex',
                    height: '100%',
                    minHeight: 0,
                    overflow: 'hidden',
                    background: '#f5f7fa'
                }}
            >
                {!isMobile ? (
                    <div
                        style={{
                            flex: filterVisible
                                ? `0 0 ${paneLayout.filterPx}px`
                                : '0 0 0',
                            width: filterVisible
                                ? paneLayout.filterPx
                                : 0,
                            minWidth: filterVisible
                                ? paneLayout.filterPx
                                : 0,
                            maxWidth: filterVisible
                                ? paneLayout.filterPx
                                : 0,
                            height: '100%',
                            minHeight: 0,
                            overflow: 'hidden',
                            flexShrink: 0
                        }}
                    >
                        <div style={{ width: '100%', height: '100%', minHeight: 0 }}>
                            {filterPanel}
                    </div>
                </div>
                ) : null}

                {!isMobile && filterVisible ? (
                    <div
                        className="tm-pane-resizer"
                        title="Тяните, чтобы изменить ширину фильтра"
                        onMouseDown={event =>
                            startPaneResize('filter', event)
                        }
                    />
                ) : null}

                <div
                    className={
                        isMobile
                            ? 'tm-page-main tm-page-main--mobile'
                            : 'tm-page-main'
                    }
                    style={{
                        flex: '1 1 auto',
                        padding: isMobile ? 8 : 16,
                        minWidth: 0,
                        height: '100%',
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}
                >
                    <Space
                        className={
                            isMobile
                                ? 'tm-page-toolbar tm-page-toolbar--mobile'
                                : 'tm-page-toolbar'
                        }
                        style={{
                            width: '100%',
                            justifyContent:
                                'space-between',
                            marginBottom: isMobile ? 8 : 12,
                            flexShrink: 0
                        }}
                        wrap
                    >
                        <Space wrap>
                            {isMobile ? (
                    <Button
                                    icon={<FilterOutlined />}
                                    onClick={() =>
                                        setMobileFilterOpen(true)
                                    }
                                >
                                    Фильтр
                                    {activeFilterCount
                                        ? ` (${activeFilterCount})`
                                        : ''}
                    </Button>
                            ) : !filterVisible ? (
                                <Button
                                    icon={
                                        <MenuUnfoldOutlined />
                                    }
                                    onClick={() =>
                                        toggleFilterVisible(true)
                                    }
                                >
                                    Показать фильтр
                                </Button>
                            ) : null}
                            <div>
                                <Title
                                    className="tm-page-title"
                                    level={isMobile ? 4 : 3}
                                    style={{
                                        margin: 0
                                    }}
                                >
                                    Менеджер задач
                                </Title>
                                <Text type="secondary">
                                    {tasks.length.toLocaleString(
                                        'ru-RU'
                                    )}{' '}
                                    на доске
                                    {total > tasks.length
                                        ? ` · всего ${total.toLocaleString('ru-RU')}`
                                        : ''}
                                    {liveConnected ? (
                                        <>
                                            {' · '}
                                            <span className="tm-kanban-live-dot" />
                                            live
                                        </>
                                    ) : (
                                        <>
                                            {' · '}
                                            офлайн
                                        </>
                                    )}
                                </Text>
                            </div>
                        </Space>

                        <Space
                            className={
                                isMobile
                                    ? 'tm-page-toolbar-actions tm-page-toolbar-actions--mobile'
                                    : 'tm-page-toolbar-actions'
                            }
                            wrap
                        >
                            {!isMobile ? (
                                <Space size={6}>
                                    <Text type="secondary">
                                        Отдельное окно
                                    </Text>
                                    <Switch
                                        checked={detailInDrawer}
                                        onChange={changeDetailMode}
                                    />
                                </Space>
                ) : null}
                            <Segmented
                                className="tm-page-view-segmented"
                                size={isMobile ? 'small' : 'middle'}
                                value={boardView}
                                onChange={changeBoardView}
                                options={
                                    isMobile
                                        ? [
                                              {
                                                  value: 'kanban',
                                                  icon: (
                                                      <AppstoreOutlined />
                                                  )
                                              },
                                              {
                                                  value: 'list',
                                                  icon: (
                                                      <UnorderedListOutlined />
                                                  )
                                              },
                                              {
                                                  value: 'map',
                                                  icon: (
                                                      <EnvironmentOutlined />
                                                  )
                                              }
                                          ]
                                        : [
                                              {
                                                  value: 'kanban',
                                                  icon: (
                                                      <AppstoreOutlined />
                                                  ),
                                                  label: 'Канбан'
                                              },
                                              {
                                                  value: 'list',
                                                  icon: (
                                                      <UnorderedListOutlined />
                                                  ),
                                                  label: 'Список'
                                              },
                                              {
                                                  value: 'map',
                                                  icon: (
                                                      <EnvironmentOutlined />
                                                  ),
                                                  label: 'Карта'
                                              }
                                          ]
                                }
                            />
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={loadTasks}
                            >
                                {isMobile ? null : 'Обновить'}
                            </Button>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                disabled={
                                    !taskAccess?.can_create
                                }
                                onClick={openCreate}
                            >
                                {isMobile ? null : 'Создать'}
                            </Button>
                        </Space>
                    </Space>

                    <div
                        style={{
                            flex: 1,
                            minHeight: 0,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                    <Spin
                        spinning={loading && boardView !== 'list'}
                        wrapperClassName="tm-board-spin"
                        style={{ height: '100%' }}
                    >
                        <div
                            style={{
                                height: '100%',
                                minHeight: 0,
                                overflow:
                                    boardView === 'kanban' ||
                                    boardView === 'list'
                                        ? 'hidden'
                                        : 'auto'
                            }}
                        >
                        {tasks.length === 0 &&
                        !loading ? (
                            <Empty description="Ничего не найдено" />
                        ) : boardView === 'list' ? (
                            <TaskManagerList
                                tasks={tasks}
                                statuses={statuses}
                                colorMap={colorMap}
                                loading={loading}
                                onTaskClick={openEdit}
                            />
                        ) : boardView === 'map' ? (
                            <TaskManagerMap
                                tasks={tasks}
                                apiKey={yandexMapsApiKey}
                                colorMap={colorMap}
                                onTaskClick={openEdit}
                            />
                        ) : (
                            <TaskManagerKanban
                                tasks={tasks}
                                statuses={statuses}
                                columnStatusNames={kanbanColumns}
                                colorMap={colorMap}
                                statusAnimIds={statusAnimIds}
                                updatedAnimIds={updatedAnimIds}
                                canEdit={Boolean(
                                    taskAccess?.can_edit
                                )}
                                onTaskClick={openEdit}
                                onStatusChange={
                                    onKanbanStatusChange
                                }
                                getAllowedTargets={
                                    resolveAllowedTargets
                                }
                                getCfPreview={getCfPreview}
                            />
                        )}
                        </div>
                    </Spin>
                    </div>
                </div>

                {splitDetail ? (
                    <>
                        <div
                            className="tm-pane-resizer"
                            title="Тяните, чтобы изменить ширину карточки"
                            onMouseDown={event =>
                                startPaneResize('detail', event)
                            }
                        />
                        <div
                            style={{
                                flex: `0 0 ${paneLayout.detailPx}px`,
                                width: paneLayout.detailPx,
                                minWidth: paneLayout.detailPx,
                                maxWidth: paneLayout.detailPx,
                                height: '100%',
                                borderLeft: '1px solid #f0f0f0',
                                background: '#fff',
                                display: 'flex',
                                flexDirection: 'column',
                                minHeight: 0,
                                overflow: 'hidden'
                            }}
                        >
                        <div
                            style={{
                                padding: '10px 12px',
                                borderBottom: '1px solid #f0f0f0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                gap: 8,
                                flexShrink: 0
                            }}
                        >
                            <div style={{ minWidth: 0, flex: 1 }}>
                                {taskDetailTitle}
                            </div>
                            <Button
                                type="text"
                                icon={<CloseOutlined />}
                                onClick={() => setDetailOpen(false)}
                            />
                        </div>
                        <div
                            style={{
                                padding: 12,
                                overflowY: 'auto',
                                flex: 1,
                                minHeight: 0
                            }}
                        >
                            {taskDetailForm}
                        </div>
                        <div
                            style={{
                                padding: 12,
                                borderTop: '1px solid #f0f0f0',
                                flexShrink: 0,
                                background: '#fff',
                                display: 'flex',
                                justifyContent: 'flex-end'
                            }}
                        >
                            {taskDetailActions}
                        </div>
                    </div>
                    </>
                ) : null}
            </div>

            {isMobile ? (
                <Drawer
                    className="tm-filter-drawer"
                    title="Фильтр"
                    placement="left"
                    width={
                        typeof window !== 'undefined'
                            ? Math.min(360, window.innerWidth)
                            : 360
                    }
                    open={mobileFilterOpen}
                    onClose={() => setMobileFilterOpen(false)}
                    styles={{
                        body: {
                            padding: 0,
                            height: '100%'
                        }
                    }}
                    destroyOnClose={false}
                >
                    {filterPanel}
                </Drawer>
            ) : null}

            {useDetailDrawer ? (
                <Drawer
                    className={
                        isMobile
                            ? 'tm-detail-drawer-mobile'
                            : undefined
                    }
                    title={taskDetailTitle}
                    placement="right"
                    width={isMobile ? '100%' : '90vw'}
                    styles={{
                        header: {
                            alignItems: 'center',
                            height: 'auto',
                            minHeight: 56
                        },
                        body: {
                            overflowY: 'auto'
                        }
                    }}
                    open={detailOpen}
                    onClose={() => setDetailOpen(false)}
                    footer={
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end'
                            }}
                        >
                            {taskDetailActions}
                        </div>
                    }
                    destroyOnClose={false}
                >
                    {taskDetailForm}
            </Drawer>
            ) : null}

            <Modal
                title="Сохранить шаблон фильтра"
                open={templateModalOpen}
                onCancel={() => setTemplateModalOpen(false)}
                onOk={saveFilterTemplate}
                confirmLoading={templateSaving}
                okText="Сохранить"
                cancelText="Отмена"
                destroyOnClose
            >
                <Text type="secondary">
                    Сохранятся выбранные поля и их текущие
                    значения
                </Text>
                <Input
                    style={{ marginTop: 12 }}
                    placeholder="Название шаблона"
                    value={templateName}
                    maxLength={80}
                    onChange={e =>
                        setTemplateName(e.target.value)
                    }
                    onPressEnter={saveFilterTemplate}
                    autoFocus
                />
            </Modal>

            <Modal
                title={
                    mailComposeMode === 'reply'
                        ? 'Ответ на письмо'
                        : 'Новое письмо'
                }
                open={mailComposeOpen}
                onCancel={() => {
                    if (!mailSending) {
                        setMailComposeOpen(false);
                    }
                }}
                onOk={submitMailCompose}
                confirmLoading={mailSending}
                okText="Отправить"
                cancelText="Отмена"
                width={720}
                destroyOnClose
                okButtonProps={{
                    icon: <SendOutlined />
                }}
            >
                <Form
                    form={mailComposeForm}
                    layout="vertical"
                    requiredMark={false}
                >
                    <Form.Item
                        name="to"
                        label="Кому"
                        rules={[
                            {
                                required: true,
                                message: 'Укажите получателя'
                            }
                        ]}
                    >
                        <Input placeholder="email@example.com" />
                    </Form.Item>
                    <Form.Item name="cc" label="Копия">
                        <Input placeholder="необязательно" />
                    </Form.Item>
                    <Form.Item
                        name="subject"
                        label="Тема"
                        rules={[
                            {
                                required: true,
                                message: 'Укажите тему'
                            }
                        ]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="text"
                        label="Текст"
                        rules={[
                            {
                                required: true,
                                message: 'Укажите текст письма'
                            }
                        ]}
                    >
                        <TextArea
                            rows={14}
                            style={{
                                fontFamily:
                                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                fontSize: 13
                            }}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </Layout>
    );
};

export default TaskManagerPage;
